import { ReactElement, useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import Plotly from 'plotly.js-dist-min';
import Spinner from '../../../../components/Spinner';
import { LoadingOverlay } from '../../../../components/ChartCard';
import { useEquipmentCatalog } from '../../../../hooks/useEquipment';
import { useSessionCalcData } from '../../../../hooks/useSessions';
import TyreDeltaBarChartApex from 'components/charts/TyreDeltaBarChartApex';
import { calculateLaps, calculateLapTimestamps } from './lapUtils';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { jStat } from 'jstat';
import { PALETTE } from './plotConstants';
import { HiOutlineDocumentArrowDown } from 'react-icons/hi2';
import { calculateYAxisParams } from '../../../../utils/plotUtils';
import { executeExportHtml } from '../../../../utils/printUtils';
import { parse, isValid } from 'date-fns';

const COLORS = [
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#ff7c7c',
  '#8dd1e1',
  '#d084d0',
  '#ffb347',
  '#67b7dc',
  '#a4de6c',
  '#ffd93d',
];

import {
  PrintDataTable,
  buildPrintDataRows,
  getStandardCellValue,
} from '../../../../components/PrintDataTable';
import { getPrintDataColumnsTyrePage1, PRINT_DATA_COLUMNS_TYRE_PAGE2 } from './tyrePrintColumns';
import { toRecordingZoneIso } from '../../../../utils/sessionTime';

function TyrePrintDataTable({ configs, session }: { configs: any[]; session: any }): ReactElement {
  // Get dynamic values from session
  const airspeedKph = session?.airspeed_ref ? Math.round(session.airspeed_ref * 3.6) : 100;
  const temperatureRef = session?.temperature_ref ?? 20;

  // Generate columns with dynamic headers
  const columnsPage1 = getPrintDataColumnsTyrePage1(airspeedKph, temperatureRef);

  // Build rows using shared utility
  const rows = buildPrintDataRows(configs);

  return (
    <PrintDataTable
      rows={rows}
      columnsPage1={columnsPage1}
      columnsPage2={PRINT_DATA_COLUMNS_TYRE_PAGE2}
      titlePage1="Detailed Run Data - Crr & Power"
      titlePage2="Detailed Run Data - Speed, Position & Calibration"
      getCellValue={getStandardCellValue}
      className="screen-data-table"
    />
  );
}

// Helper functions for common operations
const getTyreName = (tyreId: string, equipmentCatalog: any) => {
  if (!tyreId) {
    return 'Unknown Tyre';
  }

  if (equipmentCatalog && equipmentCatalog.tyres && Array.isArray(equipmentCatalog.tyres)) {
    const tyre = equipmentCatalog.tyres.find((t: any) => t.id === tyreId);
    if (tyre) {
      // Prefer name, then designation, then a formatted version of the ID
      const tyreName = tyre.name || tyre.designation;
      if (tyreName) {
        return tyreName;
      }
    }
  }

  // If no catalog match, return a shortened version of the ID
  return `Tyre ${tyreId.slice(0, 8)}`;
};

const convertPressure = (pressureBar: number, pressureUnit: 'Bar' | 'PSI') => {
  return pressureUnit === 'PSI' ? pressureBar / 0.06895 : pressureBar;
};

// Helper function to get combined tyre name (front/rear or just rear if same)
const getCombinedTyreName = (frontId: string | null | undefined, rearId: string, equipmentCatalog: any) => {
  const rearName = getTyreName(rearId, equipmentCatalog);

  if (!frontId || frontId === rearId) {
    // Same tyre front and rear, or no front tyre specified
    return rearName;
  }

  const frontName = getTyreName(frontId, equipmentCatalog);
  return `${frontName} / ${rearName}`;
};

const createPressureHoverTemplate = (pressureUnit: 'Bar' | 'PSI', yLabel: string, includeConfig = false) => {
  return (
    '<b>%{fullData.name}</b><br>' +
    `Pressure: %{x} ${pressureUnit}<br>` +
    `${yLabel}: %{y:.5f}<br>` +
    (includeConfig ? 'Config: %{customdata}<br>' : '') +
    '<extra></extra>'
  );
};

// Rolling Resistance Plot Component
function RollingResistancePlot({
  configs,
  confidenceLevel,
  setConfidenceLevel,
  session,
  dataType,
  equipmentCatalog,
  yAxisScale,
  setYAxisScale,
}: {
  configs: any[];
  confidenceLevel: string;
  setConfidenceLevel: (level: string) => void;
  session: any;
  dataType: 'traverses' | 'laps';
  equipmentCatalog: any;
  yAxisScale: number | 'auto';
  setYAxisScale: (scale: number | 'auto') => void;
}): ReactElement {
  const powerKey = 'RollingResistanceNormalised_PG5'; // Rolling Resistance in Watts
  let min = 100;
  let max = 0;
  const series = [];
  const colors = [];
  const labelColors: string[] = [];
  const sizes: number[] = [];
  const dashArray: number[] = [];
  const allYValues: number[] = [];
  const laps: number[] = [];
  const configCIs: Array<{ index: number; upper: number; lower: number; color: string }> = [];

  // Check if session is a traverse type
  const isTraverseSession = session?.analysis_type >= 1000 && session?.analysis_type < 2000;

  // Filter out configs that should be excluded and discarded runs
  const validConfigs = configs.filter((config: any) =>
    !config.exclude_from_plot &&
    config.letter !== 'DISC' &&
    !config.letter?.startsWith('DISC')
  );

  let configIndex = 0;
  for (const config of validConfigs) {
    const crrValues = [];

    // Collect all traverse values for this config
    const configTraverses: number[] = [];
    for (const run of config.runs || []) {
      const traverses = run.traverse?.[powerKey] || [];
      for (const powerValue of traverses) {
        if (powerValue !== null && powerValue !== undefined) {
          configTraverses.push(powerValue);
        }
      }
    }

    // Calculate laps for confidence interval (only for first config)
    if (configIndex === 0 && configTraverses.length > 0) {
      const configLaps = calculateLaps(configTraverses, isTraverseSession);
      laps.push(...configLaps);
    }

    // Determine what values to plot based on dataType
    const valuesToPlot = dataType === 'laps' ? calculateLaps(configTraverses, isTraverseSession) : configTraverses;
    const numValues = valuesToPlot.length;

    // Plot the values
    const spread = 0.5 / numValues;
    for (let i = 0; i < valuesToPlot.length; i++) {
      const value = valuesToPlot[i];
      if (value !== null && value !== undefined) {
        if (value < min) min = value;
        if (value > max) max = value;
        allYValues.push(value);
      }
      crrValues.push([configIndex + 0.25 + i * spread, value]);
    }

    if (crrValues.length > 0) {
      // Get description (or tyre name) and pressure for legend
      const configDescription = config.description
        || getCombinedTyreName(config.tyre_front_id, config.tyre_rear_id, equipmentCatalog);
      const tyrePressure = config.tyre_rear_pressure;
      const configLabel = `${config.letter}: ${configDescription} ${tyrePressure ? `- ${tyrePressure.toFixed(1)} Bar` : ''}`;

      series.push({ name: configLabel, type: 'scatter', data: crrValues });
      const average = config.average?.[powerKey]?.[0];
      series.push({
        name: '',
        type: 'line',
        data: [
          [configIndex + 0.25, average],
          [configIndex + 0.75, average],
        ],
      });
      const color = PALETTE[configIndex % PALETTE.length];
      colors.push(color, color);
      labelColors.push(color, '#FFFFFF00');
      sizes.push(6, 0);
      dashArray.push(0, 0);
      configIndex++;
    }
  }

  // Calculate y-axis range based on selected scale
  const yAxisParams = calculateYAxisParams(allYValues, yAxisScale);


  // Calculate mean and standard deviation of laps for confidence interval
  let confidenceIntervalText = '';
  let confidenceLevelText = '';
  let mean = 0;
  let stdDev = 0;
  if (laps.length > 0) {
    mean = laps.reduce((sum, val) => sum + val, 0) / laps.length;
    const variance = laps.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / laps.length;
    stdDev = Math.sqrt(variance);
    const lapTypeText = isTraverseSession ? 'Laps (Traverse Pairs)' : 'Laps';
    confidenceIntervalText = `Rolling Resistance Standard Deviation of ${lapTypeText}: ${stdDev.toFixed(2)} W.`;
  }

  // Calculate confidence interval for selected level
  let ciLowerBound = 0;
  let ciUpperBound = 0;
  if (confidenceLevel !== 'none' && laps.length > 1) {
    const n = laps.length;
    const df = n - 1;
    const standardError = stdDev / Math.sqrt(n);

    const conf = Number(confidenceLevel) / 100;
    const alpha = 1 - conf;
    const critical = jStat.studentt.inv(1 - alpha / 2, df);

    const ciMargin = critical * standardError;
    ciLowerBound = mean - ciMargin;
    ciUpperBound = mean + ciMargin;
    confidenceLevelText =
      `${confidenceLevel}% Confidence Interval of Mean: ${ciLowerBound.toFixed(1)} - ` +
      `${ciUpperBound.toFixed(1)} W (±${ciMargin.toFixed(2)} W)`;

    // Calculate confidence intervals for non-Config A configs
    const zScore = jStat.normal.inv(1 - alpha / 2, 0, 1);
    const baselineSD = stdDev;

    for (let i = 1; i < validConfigs.length; i++) {
      const config = validConfigs[i];
      const configMean = config.average?.[powerKey]?.[0];
      if (configMean) {
        const configTraverses: number[] = [];
        for (const run of config.runs || []) {
          const traverses = run.traverse?.[powerKey] || [];
          for (const powerValue of traverses) {
            if (powerValue !== null && powerValue !== undefined) {
              configTraverses.push(powerValue);
            }
          }
        }
        const configLaps = calculateLaps(configTraverses, isTraverseSession);
        const lapCount = configLaps.length;

        let sdToUse = baselineSD;
        if (lapCount > 2) {
          const configMeanCalc = configLaps.reduce((sum, val) => sum + val, 0) / lapCount;
          const configVariance = configLaps.reduce((sum, val) => sum + Math.pow(val - configMeanCalc, 2), 0) / lapCount;
          const configSD = Math.sqrt(configVariance);
          sdToUse = Math.max(configSD, baselineSD);
        } else if (lapCount === 1) {
          sdToUse = 2.5 * baselineSD;
        }

        const configCIMargin = zScore * sdToUse / Math.sqrt(lapCount);
        const upper = config.average[powerKey][0] + configCIMargin;
        const lower = config.average[powerKey][0] - configCIMargin;
        const color = PALETTE[i % PALETTE.length];
        configCIs.push({ index: i, upper, lower, color });
      }
    }

    // Add config-specific confidence interval lines
    for (const ci of configCIs) {
      series.push({
        name: '',
        type: 'line',
        data: [
          [ci.index + 0.25, ci.upper],
          [ci.index + 0.75, ci.upper],
        ],
      });
      colors.push(ci.color);
      labelColors.push('#FFFFFF00');
      sizes.push(0);
      dashArray.push(5);

      series.push({
        name: '',
        type: 'line',
        data: [
          [ci.index + 0.25, ci.lower],
          [ci.index + 0.75, ci.lower],
        ],
      });
      colors.push(ci.color);
      labelColors.push('#FFFFFF00');
      sizes.push(0);
      dashArray.push(5);
    }
  }

  const options: ApexOptions = {
    chart: { height: 350, zoom: { enabled: false }, animations: { enabled: false } },
    stroke: {
      dashArray: dashArray,
      width: 2,
    },
    tooltip: { enabled: false },
    xaxis: { tickAmount: 10, min: 0, max: validConfigs.length, labels: { show: false } },
    yaxis: {
      tickAmount: yAxisParams.tickAmount,
      min: yAxisParams.yMin,
      max: yAxisParams.yMax,
      labels: { formatter: (val: number) => val.toFixed(1) },
      title: {
        text: 'Watts',
        style: {
          fontSize: '14px',
          fontWeight: 600,
        },
      },
    },
    annotations: {
      xaxis: validConfigs.map((config: any, index: number) => {
        const tyreName = getCombinedTyreName(config.tyre_front_id, config.tyre_rear_id, equipmentCatalog);
        const tyrePressure = config.tyre_rear_pressure;
        const description = `${config.letter}: ${config.description || tyreName}${tyrePressure ? ` ${tyrePressure.toFixed(1)} Bar` : ''}`;
        const label = description.length > 30 ? `${description.slice(0, 30).trim()}...` : description;
        const color = PALETTE[index % PALETTE.length];
        return {
          x: index + 0.5,
          strokeDashArray: 0,
          label: {
            orientation: 'horizontal',
            position: 'bottom',
            borderColor: color,
            style: { color: '#fff', background: color, fontSize: '14px', fontWeight: 400 },
            text: label,
          },
        };
      }),
      yaxis: (() => {
        const annotations = [];
        if (confidenceLevel !== 'none' && laps.length > 0) {
          annotations.push(
            {
              y: ciUpperBound,
              borderColor: PALETTE[0],
              strokeDashArray: 5,
              borderWidth: 1,
            },
            {
              y: ciLowerBound,
              borderColor: PALETTE[0],
              strokeDashArray: 5,
              borderWidth: 1,
            }
          );
        }
        return annotations;
      })(),
    },
    markers: { size: sizes },
    legend: { show: false, markers: { fillColors: labelColors } },
    colors: colors,
  };

  // Don't render if no valid data
  if (series.length === 0 || validConfigs.length === 0) {
    return null;
  }

  return (
    <div className="cr-bg-white cr-rounded-lg cr-shadow cr-p-6 cr-mt-6">
      {/* Y-axis Scale Selector */}
      <div className="cr-mb-4 cr-flex cr-items-center cr-gap-2">
        <span className="cr-font-medium cr-text-gray-700">Y-Axis Scale (Watts):</span>
        <select
          value={yAxisScale}
          onChange={(e) => {
            const newValue = e.target.value === 'auto' ? 'auto' : parseFloat(e.target.value);
            setYAxisScale(newValue);
          }}
          className="cr-cursor-pointer cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-1 cr-text-lg"
        >
          <option value="auto">Auto</option>
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="30">30</option>
          <option value="40">40</option>
          <option value="50">50</option>
        </select>
      </div>

      <Chart options={options} series={series} type="line" width="100%" height="380" />
      {confidenceIntervalText && (
        <div className="cr-mt-2 cr-text-left cr-text-lg cr-text-gray-700">
          {confidenceIntervalText}
          <br />
          <div className="cr-mt-2 cr-flex cr-items-center cr-gap-2">
            <span>Show Confidence Interval:</span>
            <select
              value={confidenceLevel}
              onChange={(e) => setConfidenceLevel(e.target.value)}
              className="cr-cursor-pointer cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-1"
            >
              <option value="none">None</option>
              <option value="68">68%</option>
              <option value="80">80%</option>
              <option value="85">85%</option>
              <option value="90">90%</option>
              <option value="95">95%</option>
            </select>
          </div>
          {confidenceLevel !== 'none' && (
            <>
              {confidenceLevelText}
              <br />
              Dashed lines on plot show confidence. Further consistent repeats will narrow this interval
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function TyrePlots(): ReactElement {
  const { session: baseSession, sessionId } = useOutletContext<{
    sessionId: string;
    session: any;
  }>();
  // TyrePlots needs the tyre-plot-only delta variants — deltas_rolling /
  // deltas_traverse_rolling (Moving toggle) and deltas_self (temp-vs-CRR plots) —
  // which the base full payload omits. Fetch the augmented variant and prefer it
  // once loaded; the (guard-guaranteed) base session covers everything else meanwhile.
  const { data: rollingSession } = useSessionCalcData(sessionId, { includeRolling: true });
  const session = rollingSession ?? baseSession;

  const { data: equipmentCatalog } = useEquipmentCatalog();
  // Export state
  const [isExportPreparing, setIsExportPreparing] = useState(false);
  const [exportFlash, setExportFlash] = useState(false);
  const [showExportPopup, setShowExportPopup] = useState(false);
  const printContainerRef = useRef<HTMLDivElement>(null);

  // Initialize state from localStorage or use defaults
  const [pressureUnit, setPressureUnit] = useState<'Bar' | 'PSI'>(() => {
    const stored = localStorage.getItem('tyrePlots_pressureUnit');
    return stored === 'PSI' || stored === 'Bar' ? stored : 'Bar';
  });

  const [deltaAverage, setDeltaAverage] = useState<'Constant' | 'Moving'>(() => {
    const stored = localStorage.getItem('tyrePlots_deltaAverage');
    return stored === 'Moving' || stored === 'Constant' ? stored : 'Constant';
  });

  // Check if session is a traverse type (1000 <= analysis_type < 2000)
  const isTraverseSession = session?.analysis_type >= 1000 && session?.analysis_type < 2000;

  // Initialize dataType from localStorage or default to 'laps'
  const [dataType, setDataType] = useState<'traverses' | 'laps'>(() => {
    const stored = localStorage.getItem('cdaDataType');
    if (stored && (stored === 'traverses' || stored === 'laps')) {
      // If saved value is 'traverses' but session is not traverse type, use 'laps'
      if (stored === 'traverses' && !isTraverseSession) {
        return 'laps';
      }
      return stored as 'traverses' | 'laps';
    }
    return 'laps';
  });

  // Save pressure unit to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('tyrePlots_pressureUnit', pressureUnit);
  }, [pressureUnit]);

  // Save delta average to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('tyrePlots_deltaAverage', deltaAverage);
  }, [deltaAverage]);

  // Save dataType to localStorage when it changes (shared with Charts page)
  useEffect(() => {
    localStorage.setItem('cdaDataType', dataType);
  }, [dataType]);

  // Initialize confidence level from localStorage or default to '68'
  const [confidenceLevel, setConfidenceLevel] = useState<string>(() => {
    const saved = localStorage.getItem('tyreConfidenceLevel');
    return saved || '68';
  });

  // Save confidence level to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('tyreConfidenceLevel', confidenceLevel);
  }, [confidenceLevel]);

  // Initialize Y-axis scale from localStorage or default to 'auto'
  const [tyreYAxisScale, setTyreYAxisScale] = useState<number | 'auto'>(() => {
    const saved = localStorage.getItem('tyreYAxisScale');
    if (saved === 'auto') return 'auto';
    const parsed = parseFloat(saved || '');
    return !isNaN(parsed) ? parsed : 'auto';
  });

  // Save Y-axis scale to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('tyreYAxisScale', tyreYAxisScale.toString());
  }, [tyreYAxisScale]);

  const rawTimePlotRef = useRef<HTMLDivElement>(null);
  const timePlotRef = useRef<HTMLDivElement>(null);
  const deltaTimePlotRef = useRef<HTMLDivElement>(null);
  const pressurePlotRef = useRef<HTMLDivElement>(null);
  const psiPlotRef = useRef<HTMLDivElement>(null);
  const deltaPlotRef = useRef<HTMLDivElement>(null);
  const tempCrrPlotRef = useRef<HTMLDivElement>(null);
  const tempCrrG5PlotRef = useRef<HTMLDivElement>(null);
  const [isRawTimePlotLoading, setIsRawTimePlotLoading] = useState(true);
  const [isTimePlotLoading, setIsTimePlotLoading] = useState(true);
  const [isDeltaTimePlotLoading, setIsDeltaTimePlotLoading] = useState(true);
  const [, setIsPressurePlotLoading] = useState(true);
  const [, setIsPsiPlotLoading] = useState(true);
  const [isDeltaPlotLoading, setIsDeltaPlotLoading] = useState(true);
  const [isTempCrrPlotLoading, setIsTempCrrPlotLoading] = useState(true);
  const [isTempCrrG5PlotLoading, setIsTempCrrG5PlotLoading] = useState(true);

  // Extract response channels data for time-based plot
  const timePlotData = useMemo(() => {
    if (!session?.configs) {
      return { traces: [], customLegendData: {}, missingData: 'No session configs available' };
    }

    // First pass: collect all unique tyre types and their pressures
    const tyreData: { [tyreName: string]: { pressures: number[]; configs: any[] } } = {};

    session.configs.forEach((config: any) => {
      if (!config.runs || config.runs.length === 0) return;

      const tyreId = config.tyre_rear_id;
      const tyrePressure = config.tyre_rear_pressure;
      if (!tyreId || tyrePressure === null || tyrePressure === undefined) return;

      const tyreName = config.description || getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);

      if (!tyreData[tyreName]) {
        tyreData[tyreName] = { pressures: [], configs: [] };
      }

      if (!tyreData[tyreName].pressures.includes(tyrePressure)) {
        tyreData[tyreName].pressures.push(tyrePressure);
      }
      tyreData[tyreName].configs.push({ ...config, tyrePressure, tyreName });
    });

    // Sort pressures for each tyre type and assign markers
    const markerSymbols = ['circle', 'triangle-up', 'square', 'diamond', 'x'];
    const tyrePressureMarkers: { [tyreName: string]: { [pressure: number]: string } } = {};

    Object.entries(tyreData).forEach(([tyreName, data]) => {
      const sortedPressures = [...data.pressures].sort((a, b) => a - b);
      tyrePressureMarkers[tyreName] = {};
      sortedPressures.forEach((pressure, index) => {
        // Map pressure to marker based on its position in sorted array
        const markerIndex = Math.min(index, markerSymbols.length - 1);
        tyrePressureMarkers[tyreName][pressure] = markerSymbols[markerIndex];
      });
    });

    const traces: any[] = [];
    const tyreColorMap: { [tyreName: string]: string } = {};
    let colorIndex = 0;

    // Assign colors to tyre types
    Object.keys(tyreData).forEach((tyreName) => {
      tyreColorMap[tyreName] = COLORS[colorIndex % COLORS.length];
      colorIndex++;
    });

    // Now process configs and create traces
    session.configs.forEach((config: any) => {
      if (!config.runs) {
        return;
      }

      // Get tyre info from config
      const tyreId = config.tyre_rear_id;
      const tyrePressure = config.tyre_rear_pressure;
      if (!tyreId || tyrePressure === null || tyrePressure === undefined) {
        return;
      }

      const tyreName = config.description || getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);
      const color = tyreColorMap[tyreName];
      const markerSymbol = tyrePressureMarkers[tyreName]?.[tyrePressure] || 'circle';

      // Format pressure (convert to bar if needed or keep as is)
      const pressureDisplay = `${tyrePressure} Bar`;

      // Create legend name in the format: [Tyre Name] - [Pressure] - Cfg [Letter]
      const legendName = `${tyreName} - ${pressureDisplay} - Cfg ${config.letter}`;

      // First, collect all traverse data for this config
      const allTraverseValues: number[] = [];
      const allTraverseTimestamps: number[] = [];
      const traverseToRunMap: number[] = []; // Track which run each traverse belongs to

      config.runs.forEach((run: any, runIndex: number) => {
        if (!run.traverse) return;

        const runTimestamp = run.average?.['run_ts'];
        const crrCalcTempNormData = run.traverse['Crr_xG5'];

        if (runTimestamp && crrCalcTempNormData) {
          const originalTime = new Date(runTimestamp);
          const timeIntervalSeconds = 30;

          for (let i = 0; i < crrCalcTempNormData.length; i++) {
            if (crrCalcTempNormData[i] !== null && crrCalcTempNormData[i] !== undefined) {
              allTraverseValues.push(crrCalcTempNormData[i]);
              const progressiveTimeMs = originalTime.getTime() + i * timeIntervalSeconds * 1000;
              allTraverseTimestamps.push(progressiveTimeMs);
              traverseToRunMap.push(runIndex + 1);
            }
          }
        }
      });

      // Now process the data based on dataType
      const displayPoints: { x: string; y: number; run: number }[] = [];

      if (dataType === 'laps' && allTraverseValues.length > 0) {
        // Calculate lap averages and timestamps
        const lapValues = calculateLaps(allTraverseValues, isTraverseSession);
        const lapTimestamps = calculateLapTimestamps(allTraverseTimestamps, isTraverseSession);

        for (let i = 0; i < lapValues.length; i++) {
          displayPoints.push({
            x: toRecordingZoneIso(new Date(lapTimestamps[i]), config.runs[0]?.timezone),
            y: lapValues[i],
            run: Math.floor(i / (isTraverseSession ? 1 : 1)) + 1, // Approximate run number
          });
        }
      } else if (dataType === 'traverses') {
        // Show individual traverse points
        for (let i = 0; i < allTraverseValues.length; i++) {
          displayPoints.push({
            x: toRecordingZoneIso(new Date(allTraverseTimestamps[i]), config.runs[0]?.timezone),
            y: allTraverseValues[i],
            run: traverseToRunMap[i],
          });
        }
      }

      // Collect run averages (always shown)
      const averagePoints: { x: string; y: number; run: number }[] = [];
      config.runs.forEach((run: any, runIndex: number) => {
        if (!run.traverse) return;

        const runTimestamp = run.average?.['run_ts'];
        const crrCalcTempNormData = run.traverse['Crr_xG5'];
        const runAverage = run.average?.['Crr_xG5'];

        if (runTimestamp && crrCalcTempNormData && runAverage !== null && runAverage !== undefined) {
          const originalTime = new Date(runTimestamp);
          const timeIntervalSeconds = 30;
          const midTimeMs =
            originalTime.getTime() + Math.floor(crrCalcTempNormData.length / 2) * timeIntervalSeconds * 1000;
          const midTime = new Date(midTimeMs);

          const avgValue = Array.isArray(runAverage) ? runAverage[0] : runAverage;
          averagePoints.push({
            x: toRecordingZoneIso(midTime, run.timezone),
            y: avgValue,
            run: runIndex + 1,
          });
        }
      });

      // Add display points trace (traverses or laps based on selection)
      if (displayPoints.length > 0) {
        const pointLabel = dataType === 'laps' ? 'Lap' : 'Traverse';
        traces.push({
          x: displayPoints.map((p) => p.x),
          y: displayPoints.map((p) => p.y),
          mode: 'markers',
          type: 'scatter',
          name: `${tyreName}_${tyrePressure}_${dataType}`,
          legendgroup: tyreName,
          marker: {
            color: color,
            size: dataType === 'laps' ? 8 : 6,
            opacity: dataType === 'laps' ? 0.7 : 0.5,
            symbol: markerSymbol,
          },
          hovertemplate:
            `<b>Config ${config.letter}</b><br>` +
            'Time: %{x}<br>' +
            `Crr (${pointLabel}): %{y:.5f}<br>` +
            (dataType === 'traverses' ? 'Run: %{customdata}<br>' : '') +
            '<extra></extra>',
          customdata: displayPoints.map((p) => p.run),
          showlegend: false,
        });
      }

      // Add average points trace (prominent markers with lines)
      if (averagePoints.length > 0) {
        // Sort by time for proper line connection
        averagePoints.sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime());

        traces.push({
          x: averagePoints.map((p) => p.x),
          y: averagePoints.map((p) => p.y),
          mode: 'markers+lines',
          type: 'scatter',
          name: `${tyreName}_${tyrePressure}_avg`,
          legendgroup: tyreName,
          marker: {
            color: color,
            size: 14,
            opacity: 1.0,
            symbol: markerSymbol,
            line: {
              width: 2,
              color: 'white',
            },
          },
          line: {
            color: color,
            width: 3,
            dash: 'solid',
          },
          hovertemplate:
            `<b>${legendName}</b><br>` +
            'Time: %{x}<br>' +
            'Crr Average: %{y:.5f}<br>' +
            'Run: %{customdata}<br>' +
            '<extra></extra>',
          customdata: averagePoints.map((p) => p.run),
          showlegend: false,
        });
      }
    });

    // Create custom legend data structure
    const customLegendData: { [tyreName: string]: { pressures: number[]; colors: string[]; symbols: string[] } } = {};

    session.configs?.forEach((config: any) => {
      const tyrePressure = config.tyre_rear_pressure;
      const tyreId = config.tyre_rear_id;

      if (!tyrePressure || !tyreId) return;

      const tyreName = config.description || getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);
      const color = tyreColorMap[tyreName];
      const symbol = tyrePressureMarkers[tyreName]?.[tyrePressure] || 'circle';

      if (!customLegendData[tyreName]) {
        customLegendData[tyreName] = { pressures: [], colors: [], symbols: [] };
      }

      if (!customLegendData[tyreName].pressures.includes(tyrePressure)) {
        customLegendData[tyreName].pressures.push(tyrePressure);
        customLegendData[tyreName].colors.push(color);
        customLegendData[tyreName].symbols.push(symbol);
      }
    });

    // Check if we have any data
    let missingData = undefined;
    if (traces.length === 0) {
      const hasConfigs = session.configs && session.configs.length > 0;
      const hasValidTyreData = session.configs?.some((c: any) => c.tyre_rear_id && c.tyre_rear_pressure !== null);
      const hasTraverseData = session.configs?.some((c: any) =>
        c.runs?.some((r: any) => r.traverse && r.traverse['Crr_xG5']),
      );

      if (!hasConfigs) {
        missingData = 'No configs found in session';
      } else if (!hasValidTyreData) {
        missingData = 'Tyre information (tyre_rear_id or tyre_rear_pressure) is missing from configs';
      } else if (!hasTraverseData) {
        missingData = 'Crr_xG5 data is missing from run traverse data';
      } else {
        missingData = 'Required tyre plot data is not available';
      }
    }

    return { traces, customLegendData, missingData };
  }, [session, equipmentCatalog, dataType, isTraverseSession]);

  // Extract response channels data for raw (non-temperature-adjusted) time-based plot using Crr_xG3
  const rawTimePlotData = useMemo(() => {
    if (!session?.configs) {
      return { traces: [], customLegendData: {}, missingData: 'No session configs available' };
    }

    // First pass: collect all unique tyre types and their pressures
    const tyreData: { [tyreName: string]: { pressures: number[]; configs: any[] } } = {};

    session.configs.forEach((config: any) => {
      if (!config.runs || config.runs.length === 0) return;

      const tyreId = config.tyre_rear_id;
      const tyrePressure = config.tyre_rear_pressure;
      if (!tyreId || tyrePressure === null || tyrePressure === undefined) return;

      const tyreName = config.description || getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);

      if (!tyreData[tyreName]) {
        tyreData[tyreName] = { pressures: [], configs: [] };
      }

      if (!tyreData[tyreName].pressures.includes(tyrePressure)) {
        tyreData[tyreName].pressures.push(tyrePressure);
      }
      tyreData[tyreName].configs.push({ ...config, tyrePressure, tyreName });
    });

    // Sort pressures for each tyre type and assign markers
    const markerSymbols = ['circle', 'triangle-up', 'square', 'diamond', 'x'];
    const tyrePressureMarkers: { [tyreName: string]: { [pressure: number]: string } } = {};

    Object.entries(tyreData).forEach(([tyreName, data]) => {
      const sortedPressures = [...data.pressures].sort((a, b) => a - b);
      tyrePressureMarkers[tyreName] = {};
      sortedPressures.forEach((pressure, index) => {
        const markerIndex = Math.min(index, markerSymbols.length - 1);
        tyrePressureMarkers[tyreName][pressure] = markerSymbols[markerIndex];
      });
    });

    const traces: any[] = [];
    const tyreColorMap: { [tyreName: string]: string } = {};
    let colorIndex = 0;

    // Assign colors to tyre types
    Object.keys(tyreData).forEach((tyreName) => {
      tyreColorMap[tyreName] = COLORS[colorIndex % COLORS.length];
      colorIndex++;
    });

    // Now process configs and create traces
    session.configs.forEach((config: any) => {
      if (!config.runs) {
        return;
      }

      const tyreId = config.tyre_rear_id;
      const tyrePressure = config.tyre_rear_pressure;
      if (!tyreId || tyrePressure === null || tyrePressure === undefined) {
        return;
      }

      const tyreName = config.description || getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);
      const color = tyreColorMap[tyreName];
      const markerSymbol = tyrePressureMarkers[tyreName]?.[tyrePressure] || 'circle';
      const pressureDisplay = `${tyrePressure} Bar`;
      const legendName = `${tyreName} - ${pressureDisplay} - Cfg ${config.letter}`;

      // First, collect all traverse data for this config using Crr_xG3 (raw)
      const allTraverseValues: number[] = [];
      const allTraverseTimestamps: number[] = [];
      const traverseToRunMap: number[] = [];

      config.runs.forEach((run: any, runIndex: number) => {
        if (!run.traverse) return;

        const runTimestamp = run.average?.['run_ts'];
        // Use Crr_xG3 if available, otherwise fall back to Crr_xG5
        const crrRawData = run.traverse['Crr_xG3'] || run.traverse['Crr_xG5'];

        if (runTimestamp && crrRawData) {
          const originalTime = new Date(runTimestamp);
          const timeIntervalSeconds = 30;

          for (let i = 0; i < crrRawData.length; i++) {
            if (crrRawData[i] !== null && crrRawData[i] !== undefined) {
              allTraverseValues.push(crrRawData[i]);
              const progressiveTimeMs = originalTime.getTime() + i * timeIntervalSeconds * 1000;
              allTraverseTimestamps.push(progressiveTimeMs);
              traverseToRunMap.push(runIndex + 1);
            }
          }
        }
      });

      // Now process the data based on dataType
      const displayPoints: { x: string; y: number; run: number }[] = [];

      if (dataType === 'laps' && allTraverseValues.length > 0) {
        const lapValues = calculateLaps(allTraverseValues, isTraverseSession);
        const lapTimestamps = calculateLapTimestamps(allTraverseTimestamps, isTraverseSession);

        for (let i = 0; i < lapValues.length; i++) {
          displayPoints.push({
            x: toRecordingZoneIso(new Date(lapTimestamps[i]), config.runs[0]?.timezone),
            y: lapValues[i],
            run: Math.floor(i / (isTraverseSession ? 1 : 1)) + 1,
          });
        }
      } else if (dataType === 'traverses') {
        for (let i = 0; i < allTraverseValues.length; i++) {
          displayPoints.push({
            x: toRecordingZoneIso(new Date(allTraverseTimestamps[i]), config.runs[0]?.timezone),
            y: allTraverseValues[i],
            run: traverseToRunMap[i],
          });
        }
      }

      // Collect run averages using Crr_xG3 (with fallback to Crr_xG5)
      const averagePoints: { x: string; y: number; run: number }[] = [];
      config.runs.forEach((run: any, runIndex: number) => {
        if (!run.traverse) return;

        const runTimestamp = run.average?.['run_ts'];
        const crrRawData = run.traverse['Crr_xG3'] || run.traverse['Crr_xG5'];
        const runAverage = run.average?.['Crr_xG3'] ?? run.average?.['Crr_xG5'];

        if (runTimestamp && crrRawData && runAverage !== null && runAverage !== undefined) {
          const originalTime = new Date(runTimestamp);
          const timeIntervalSeconds = 30;
          const midTimeMs =
            originalTime.getTime() + Math.floor(crrRawData.length / 2) * timeIntervalSeconds * 1000;
          const midTime = new Date(midTimeMs);

          const avgValue = Array.isArray(runAverage) ? runAverage[0] : runAverage;
          averagePoints.push({
            x: toRecordingZoneIso(midTime, run.timezone),
            y: avgValue,
            run: runIndex + 1,
          });
        }
      });

      // Add display points trace
      if (displayPoints.length > 0) {
        const pointLabel = dataType === 'laps' ? 'Lap' : 'Traverse';
        traces.push({
          x: displayPoints.map((p) => p.x),
          y: displayPoints.map((p) => p.y),
          mode: 'markers',
          type: 'scatter',
          name: `${tyreName}_${tyrePressure}_${dataType}_raw`,
          legendgroup: tyreName,
          marker: {
            color: color,
            size: dataType === 'laps' ? 8 : 6,
            opacity: dataType === 'laps' ? 0.7 : 0.5,
            symbol: markerSymbol,
          },
          hovertemplate:
            `<b>Config ${config.letter}</b><br>` +
            'Time: %{x}<br>' +
            `Crr Raw (${pointLabel}): %{y:.5f}<br>` +
            (dataType === 'traverses' ? 'Run: %{customdata}<br>' : '') +
            '<extra></extra>',
          customdata: displayPoints.map((p) => p.run),
          showlegend: false,
        });
      }

      // Add average points trace
      if (averagePoints.length > 0) {
        averagePoints.sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime());

        traces.push({
          x: averagePoints.map((p) => p.x),
          y: averagePoints.map((p) => p.y),
          mode: 'markers+lines',
          type: 'scatter',
          name: `${tyreName}_${tyrePressure}_avg_raw`,
          legendgroup: tyreName,
          marker: {
            color: color,
            size: 14,
            opacity: 1.0,
            symbol: markerSymbol,
            line: {
              width: 2,
              color: 'white',
            },
          },
          line: {
            color: color,
            width: 3,
            dash: 'solid',
          },
          hovertemplate:
            `<b>${legendName}</b><br>` +
            'Time: %{x}<br>' +
            'Crr Raw Average: %{y:.5f}<br>' +
            'Run: %{customdata}<br>' +
            '<extra></extra>',
          customdata: averagePoints.map((p) => p.run),
          showlegend: false,
        });
      }
    });

    // Create custom legend data structure
    const customLegendData: { [tyreName: string]: { pressures: number[]; colors: string[]; symbols: string[] } } = {};

    session.configs?.forEach((config: any) => {
      const tyrePressure = config.tyre_rear_pressure;
      const tyreId = config.tyre_rear_id;

      if (!tyrePressure || !tyreId) return;

      const tyreName = config.description || getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);
      const color = tyreColorMap[tyreName];
      const symbol = tyrePressureMarkers[tyreName]?.[tyrePressure] || 'circle';

      if (!customLegendData[tyreName]) {
        customLegendData[tyreName] = { pressures: [], colors: [], symbols: [] };
      }

      if (!customLegendData[tyreName].pressures.includes(tyrePressure)) {
        customLegendData[tyreName].pressures.push(tyrePressure);
        customLegendData[tyreName].colors.push(color);
        customLegendData[tyreName].symbols.push(symbol);
      }
    });

    // Check if we have any data
    let missingData = undefined;
    if (traces.length === 0) {
      const hasConfigs = session.configs && session.configs.length > 0;
      const hasValidTyreData = session.configs?.some((c: any) => c.tyre_rear_id && c.tyre_rear_pressure !== null);
      const hasTraverseData = session.configs?.some((c: any) =>
        c.runs?.some((r: any) => r.traverse && (r.traverse['Crr_xG3'] || r.traverse['Crr_xG5'])),
      );

      if (!hasConfigs) {
        missingData = 'No configs found in session';
      } else if (!hasValidTyreData) {
        missingData = 'Tyre information (tyre_rear_id or tyre_rear_pressure) is missing from configs';
      } else if (!hasTraverseData) {
        missingData = 'Crr data is missing from run traverse data';
      } else {
        missingData = 'Required raw tyre plot data is not available';
      }
    }

    return { traces, customLegendData, missingData };
  }, [session, equipmentCatalog, dataType, isTraverseSession]);

  // Extract response channels data for deltas time-based plot
  const deltaTimePlotData = useMemo(() => {
    if (!session?.configs) {
      return { traces: [], customLegendData: {}, missingData: 'No session configs available' };
    }

    // First pass: collect all unique tyre types and their pressures
    const tyreData: { [tyreName: string]: { pressures: number[]; configs: any[] } } = {};

    session.configs.forEach((config: any) => {
      if (!config.runs || config.runs.length === 0) return;

      const tyreId = config.tyre_rear_id;
      const tyrePressure = config.tyre_rear_pressure;
      if (!tyreId || tyrePressure === null || tyrePressure === undefined) return;

      const tyreName = config.description || getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);

      if (!tyreData[tyreName]) {
        tyreData[tyreName] = { pressures: [], configs: [] };
      }

      if (!tyreData[tyreName].pressures.includes(tyrePressure)) {
        tyreData[tyreName].pressures.push(tyrePressure);
      }
      tyreData[tyreName].configs.push({ ...config, tyrePressure, tyreName });
    });

    // Sort pressures for each tyre type and assign markers
    const markerSymbols = ['circle', 'triangle-up', 'square', 'diamond', 'x'];
    const tyrePressureMarkers: { [tyreName: string]: { [pressure: number]: string } } = {};

    Object.entries(tyreData).forEach(([tyreName, data]) => {
      const sortedPressures = [...data.pressures].sort((a, b) => a - b);
      tyrePressureMarkers[tyreName] = {};
      sortedPressures.forEach((pressure, index) => {
        const markerIndex = Math.min(index, markerSymbols.length - 1);
        tyrePressureMarkers[tyreName][pressure] = markerSymbols[markerIndex];
      });
    });

    const traces: any[] = [];
    const tyreColorMap: { [tyreName: string]: string } = {};
    let colorIndex = 0;

    // Assign colors to tyre types
    Object.keys(tyreData).forEach((tyreName) => {
      tyreColorMap[tyreName] = COLORS[colorIndex % COLORS.length];
      colorIndex++;
    });

    // Now process configs and create traces
    session.configs.forEach((config: any) => {
      if (!config.runs) {
        return;
      }

      const tyreId = config.tyre_rear_id;
      const tyrePressure = config.tyre_rear_pressure;
      if (!tyreId || tyrePressure === null || tyrePressure === undefined) return;

      const tyreName = config.description || getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);
      const color = tyreColorMap[tyreName];
      const markerSymbol = tyrePressureMarkers[tyreName]?.[tyrePressure] || 'circle';

      const pressureDisplay = `${tyrePressure} Bar`;
      const legendName = `${tyreName} - ${pressureDisplay} - Cfg ${config.letter}`;

      // First, collect all traverse delta data for this config
      const allTraverseDeltas: number[] = [];
      const allTraverseTimestamps: number[] = [];
      const traverseToRunMap: number[] = [];

      // Choose the appropriate data based on deltaAverage setting
      const traverseField = deltaAverage === 'Moving' ? 'deltas_traverse_rolling' : 'deltas_traverse';
      const averageField = deltaAverage === 'Moving' ? 'deltas_rolling' : 'deltas';

      config.runs.forEach((run: any, runIndex: number) => {
        if (!run[traverseField]) return;

        const runTimestamp = run.average?.['run_ts'];
        const deltasCrrData = run[traverseField]['Crr_xG5'];

        if (runTimestamp && deltasCrrData) {
          const originalTime = new Date(runTimestamp);
          const timeIntervalSeconds = 30;

          for (let i = 0; i < deltasCrrData.length; i++) {
            if (deltasCrrData[i] !== null && deltasCrrData[i] !== undefined) {
              allTraverseDeltas.push(deltasCrrData[i]);
              const progressiveTimeMs = originalTime.getTime() + i * timeIntervalSeconds * 1000;
              allTraverseTimestamps.push(progressiveTimeMs);
              traverseToRunMap.push(runIndex + 1);
            }
          }
        }
      });

      // Now process the data based on dataType
      const displayPoints: { x: string; y: number; run: number }[] = [];

      if (dataType === 'laps' && allTraverseDeltas.length > 0) {
        // Calculate lap averages and timestamps
        const lapDeltas = calculateLaps(allTraverseDeltas, isTraverseSession);
        const lapTimestamps = calculateLapTimestamps(allTraverseTimestamps, isTraverseSession);

        for (let i = 0; i < lapDeltas.length; i++) {
          displayPoints.push({
            x: toRecordingZoneIso(new Date(lapTimestamps[i]), config.runs[0]?.timezone),
            y: lapDeltas[i],
            run: Math.floor(i / (isTraverseSession ? 1 : 1)) + 1,
          });
        }
      } else if (dataType === 'traverses') {
        // Show individual traverse delta points
        for (let i = 0; i < allTraverseDeltas.length; i++) {
          displayPoints.push({
            x: toRecordingZoneIso(new Date(allTraverseTimestamps[i]), config.runs[0]?.timezone),
            y: allTraverseDeltas[i],
            run: traverseToRunMap[i],
          });
        }
      }

      // Collect run delta averages (always shown)
      const averagePoints: { x: string; y: number; run: number }[] = [];
      config.runs.forEach((run: any, runIndex: number) => {
        if (!run[traverseField]) return;

        const runTimestamp = run.average?.['run_ts'];
        const deltasCrrData = run[traverseField]['Crr_xG5'];
        const runDeltaAverage = run[averageField]?.['Crr_xG5'];

        if (runTimestamp && deltasCrrData && runDeltaAverage !== null && runDeltaAverage !== undefined) {
          const originalTime = new Date(runTimestamp);
          const timeIntervalSeconds = 30;
          const midTimeMs =
            originalTime.getTime() + Math.floor(deltasCrrData.length / 2) * timeIntervalSeconds * 1000;
          const midTime = new Date(midTimeMs);

          const avgValue = Array.isArray(runDeltaAverage) ? runDeltaAverage[0] : runDeltaAverage;
          averagePoints.push({
            x: toRecordingZoneIso(midTime, run.timezone),
            y: avgValue,
            run: runIndex + 1,
          });
        }
      });

      // Add display points trace (traverses or laps based on selection)
      if (displayPoints.length > 0) {
        const pointLabel = dataType === 'laps' ? 'Lap' : 'Traverse';
        traces.push({
          x: displayPoints.map((p) => p.x),
          y: displayPoints.map((p) => p.y),
          mode: 'markers',
          type: 'scatter',
          name: `${tyreName}_${tyrePressure}_${dataType}_delta`,
          legendgroup: tyreName,
          marker: {
            color: color,
            size: dataType === 'laps' ? 8 : 6,
            opacity: dataType === 'laps' ? 0.7 : 0.5,
            symbol: markerSymbol,
          },
          hovertemplate:
            `<b>Config ${config.letter}</b><br>` +
            'Time: %{x}<br>' +
            `Crr Delta (${pointLabel}): %{y:.5f}<br>` +
            (dataType === 'traverses' ? 'Run: %{customdata}<br>' : '') +
            '<extra></extra>',
          customdata: displayPoints.map((p) => p.run),
          showlegend: false,
        });
      }

      // Add average points trace (prominent markers with lines)
      if (averagePoints.length > 0) {
        averagePoints.sort((a, b) => a.x.localeCompare(b.x));

        traces.push({
          x: averagePoints.map((p) => p.x),
          y: averagePoints.map((p) => p.y),
          mode: 'markers+lines',
          type: 'scatter',
          name: `${tyreName}_${tyrePressure}_avg_delta`,
          legendgroup: tyreName,
          marker: {
            color: color,
            size: 14,
            opacity: 1.0,
            symbol: markerSymbol,
            line: {
              width: 2,
              color: 'white',
            },
          },
          line: {
            color: color,
            width: 3,
            dash: 'solid',
          },
          hovertemplate:
            `<b>${legendName}</b><br>` +
            'Time: %{x}<br>' +
            'Crr Delta Average: %{y:.5f}<br>' +
            'Run: %{customdata}<br>' +
            '<extra></extra>',
          customdata: averagePoints.map((p) => p.run),
          showlegend: false,
        });
      }
    });

    // Create custom legend data structure
    const customLegendData: { [tyreName: string]: { pressures: number[]; colors: string[]; symbols: string[] } } = {};

    session.configs?.forEach((config: any) => {
      const tyrePressure = config.tyre_rear_pressure;
      const tyreId = config.tyre_rear_id;

      if (!tyrePressure || !tyreId) return;

      const tyreName = config.description || getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);
      const color = tyreColorMap[tyreName];
      const symbol = tyrePressureMarkers[tyreName]?.[tyrePressure] || 'circle';

      if (!customLegendData[tyreName]) {
        customLegendData[tyreName] = { pressures: [], colors: [], symbols: [] };
      }

      if (!customLegendData[tyreName].pressures.includes(tyrePressure)) {
        customLegendData[tyreName].pressures.push(tyrePressure);
        customLegendData[tyreName].colors.push(color);
        customLegendData[tyreName].symbols.push(symbol);
      }
    });

    // Check if we have any data
    let missingData = undefined;
    if (traces.length === 0) {
      const deltaField = deltaAverage === 'Moving' ? 'deltas_traverse_rolling' : 'deltas_traverse';
      const hasDeltas = session.configs?.some((c: any) =>
        c.runs?.some((r: any) => r[deltaField] && r[deltaField]['Crr_xG5']),
      );

      if (!hasDeltas) {
        missingData = `Delta Crr_xG5 data is missing from run ${deltaField} data`;
      } else {
        missingData = 'Required delta tyre plot data is not available';
      }
    }

    return { traces, customLegendData, missingData };
  }, [session, equipmentCatalog, deltaAverage, dataType, isTraverseSession]);

  // Extract tyre pressure data for pressure-based plot
  const pressurePlotData = useMemo(() => {
    if (!session?.configs) {
      return [];
    }

    // Group data by tyre type
    const tyreGroups: { [tyreType: string]: any[] } = {};

    session.configs.forEach((config: any) => {
      if (!config.runs) {
        return;
      }

      // Get tyre pressure and ID from config fields
      const tyrePressureBar = config.tyre_rear_pressure;
      const tyreId = config.tyre_rear_id;

      if (!tyrePressureBar || !tyreId) {
        return;
      }

      // Convert pressure based on selected unit
      const tyrePressure = convertPressure(tyrePressureBar, pressureUnit);

      // Get tyre name from catalog using front and rear IDs
      const tyreType = config.description || getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);

      if (!tyreGroups[tyreType]) {
        tyreGroups[tyreType] = [];
      }

      // Collect all traverse values and averages across runs for this config
      const allAverageValues: number[] = [];

      config.runs.forEach((run: any) => {
        if (!run.traverse) return;

        const crrCalcTempNormData = run.traverse['Crr_xG5'];
        const configAverage = run.average?.['Crr_xG5'];

        if (crrCalcTempNormData) {
          // Add traverse points (light points)
          crrCalcTempNormData.forEach((value: number) => {
            if (value !== null && value !== undefined) {
              tyreGroups[tyreType].push({
                x: tyrePressure,
                y: value,
                type: 'traverse',
                config: config.letter,
                description: config.description,
              });
            }
          });
        }

        // Collect averages from each run
        if (configAverage !== null && configAverage !== undefined) {
          // Ensure configAverage is a number, not an array
          const avgValue = Array.isArray(configAverage) ? configAverage[0] : configAverage;
          allAverageValues.push(avgValue);
        }
      });

      // Calculate single average per config (average of all run averages)
      if (allAverageValues.length > 0) {
        const configOverallAverage = allAverageValues.reduce((sum, val) => sum + val, 0) / allAverageValues.length;
        tyreGroups[tyreType].push({
          x: tyrePressure,
          y: configOverallAverage,
          type: 'average',
          config: config.letter,
          description: config.description,
        });
      }
    });

    // Create traces for each tyre type
    const traces: any[] = [];
    let colorIndex = 0;

    Object.entries(tyreGroups).forEach(([tyreType, points]) => {
      const color = COLORS[colorIndex % COLORS.length];

      // Traverse points (light)
      const traversePoints = points.filter((p) => p.type === 'traverse');

      if (traversePoints.length > 0) {
        traces.push({
          x: traversePoints.map((p) => p.x),
          y: traversePoints.map((p) => p.y),
          mode: 'markers',
          type: 'scatter',
          name: `${tyreType} (traverse)`,
          marker: {
            color: color,
            size: 6,
            opacity: 0.3,
          },
          hovertemplate: createPressureHoverTemplate(pressureUnit, 'Crr'),
          showlegend: false,
        });
      }

      // Average points with connecting lines (heavy)
      const averagePoints = points.filter((p) => p.type === 'average');
      if (averagePoints.length > 0) {
        // Sort by pressure for proper line connection
        averagePoints.sort((a, b) => a.x - b.x);

        const averageTrace = {
          x: averagePoints.map((p) => p.x),
          y: averagePoints.map((p) => p.y),
          mode: 'markers+lines',
          type: 'scatter',
          name: `${tyreType} (average)`,
          marker: {
            color: color,
            size: 12,
            opacity: 1.0,
            symbol: 'circle',
            line: {
              width: 2,
              color: 'white',
            },
          },
          line: {
            color: color,
            width: 3,
            dash: 'solid',
          },
          hovertemplate: createPressureHoverTemplate(pressureUnit, 'Crr', true),
          customdata: averagePoints.map((p) => p.config),
          showlegend: false,
        };

        traces.push(averageTrace);
      }

      colorIndex++;
    });

    return traces;
  }, [session, equipmentCatalog, pressureUnit]);

  // Extract data for PSI-based plot with RollingResistanceNormalised_PG5
  const psiPlotData = useMemo(() => {
    if (!session?.configs) {
      return [];
    }

    // Group data by tyre type
    const tyreGroups: { [tyreType: string]: any[] } = {};

    session.configs.forEach((config: any) => {
      if (!config.runs) {
        return;
      }

      // Get tyre pressure and ID from config fields
      const tyrePressureBar = config.tyre_rear_pressure;
      const tyreId = config.tyre_rear_id;

      if (!tyrePressureBar || !tyreId) {
        return;
      }

      // Convert pressure based on selected unit
      const tyrePressure = convertPressure(tyrePressureBar, pressureUnit);

      // Get tyre name from catalog using front and rear IDs
      const tyreType = config.description || getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);

      if (!tyreGroups[tyreType]) {
        tyreGroups[tyreType] = [];
      }

      // Collect all traverse values and averages across runs for this config
      const allAverageValues: number[] = [];

      config.runs.forEach((run: any) => {
        if (!run.traverse) return;

        const rollingResistanceData = run.traverse['RollingResistanceNormalised_PG5'];
        const configAverage = run.average?.['RollingResistanceNormalised_PG5'];

        if (rollingResistanceData) {
          // Add traverse points (light points)
          rollingResistanceData.forEach((value: number) => {
            if (value !== null && value !== undefined) {
              tyreGroups[tyreType].push({
                x: tyrePressure,
                y: value,
                type: 'traverse',
                config: config.letter,
                description: config.description,
              });
            }
          });
        }

        // Collect averages from each run
        if (configAverage !== null && configAverage !== undefined) {
          // Ensure configAverage is a number, not an array
          const avgValue = Array.isArray(configAverage) ? configAverage[0] : configAverage;
          allAverageValues.push(avgValue);
        }
      });

      // Calculate single average per config (average of all run averages)
      if (allAverageValues.length > 0) {
        const configOverallAverage = allAverageValues.reduce((sum, val) => sum + val, 0) / allAverageValues.length;
        tyreGroups[tyreType].push({
          x: tyrePressure,
          y: configOverallAverage,
          type: 'average',
          config: config.letter,
          description: config.description,
        });
      }
    });

    // Create traces for each tyre type
    const traces: any[] = [];
    let colorIndex = 0;

    Object.entries(tyreGroups).forEach(([tyreType, points]) => {
      const color = COLORS[colorIndex % COLORS.length];

      // Traverse points (light)
      const traversePoints = points.filter((p) => p.type === 'traverse');

      if (traversePoints.length > 0) {
        traces.push({
          x: traversePoints.map((p) => p.x),
          y: traversePoints.map((p) => p.y),
          mode: 'markers',
          type: 'scatter',
          name: `${tyreType} (traverse)`,
          marker: {
            color: color,
            size: 6,
            opacity: 0.3,
          },
          hovertemplate: createPressureHoverTemplate(pressureUnit, 'Rolling Resistance'),
          showlegend: false,
        });
      }

      // Average points with connecting lines (heavy)
      const averagePoints = points.filter((p) => p.type === 'average');
      if (averagePoints.length > 0) {
        // Sort by pressure for proper line connection
        averagePoints.sort((a, b) => a.x - b.x);

        const averageTrace = {
          x: averagePoints.map((p) => p.x),
          y: averagePoints.map((p) => p.y),
          mode: 'markers+lines',
          type: 'scatter',
          name: `${tyreType} (average)`,
          marker: {
            color: color,
            size: 12,
            opacity: 1.0,
            symbol: 'circle',
            line: {
              width: 2,
              color: 'white',
            },
          },
          line: {
            color: color,
            width: 3,
            dash: 'solid',
          },
          hovertemplate: createPressureHoverTemplate(pressureUnit, 'Rolling Resistance', true),
          customdata: averagePoints.map((p) => p.config),
          showlegend: true,
        };

        traces.push(averageTrace);
      }

      colorIndex++;
    });

    return traces;
  }, [session, equipmentCatalog, pressureUnit]);

  // Extract data for delta plot (using server-provided deltas)
  const deltaPlotData = useMemo(() => {
    if (!session?.configs) {
      return [];
    }

    // Collect delta data from all configs
    const traces: any[] = [];
    const configData: any[] = [];

    session.configs.forEach((config: any) => {
      if (!config.runs || config.runs.length === 0) return;

      // Get tyre info
      const tyrePressureBar = config.tyre_rear_pressure;
      const tyreId = config.tyre_rear_id;

      if (!tyrePressureBar || !tyreId) return;

      // Convert pressure based on selected unit
      const tyrePressure = convertPressure(tyrePressureBar, pressureUnit);
      const tyreName = config.description || getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);

      // Use server-provided delta values
      const configValues: number[] = [];

      config.runs.forEach((run: any) => {
        // Use deltas field from server
        const deltaValue = run.deltas?.['RollingResistanceNormalised_PG5'];
        if (deltaValue !== null && deltaValue !== undefined) {
          const value = Array.isArray(deltaValue) ? deltaValue[0] : deltaValue;
          configValues.push(value);
        }
      });

      if (configValues.length > 0) {
        // Average the delta values from all runs
        const avgDelta = configValues.reduce((sum, val) => sum + val, 0) / configValues.length;

        configData.push({
          x: tyrePressure,
          y: avgDelta,
          config: config.letter,
          letterNumber: config.letter_number,
          tyreName: tyreName,
          tyrePressure: tyrePressureBar,
          description: config.description,
        });
      }
    });

    // Sort by config letter_number for consistent ordering
    configData.sort((a, b) => a.letterNumber - b.letterNumber);

    // Group by tyre type for coloring
    const tyreGroups: { [tyreName: string]: any[] } = {};
    configData.forEach((point) => {
      if (!tyreGroups[point.tyreName]) {
        tyreGroups[point.tyreName] = [];
      }
      tyreGroups[point.tyreName].push(point);
    });

    let colorIndex = 0;
    Object.entries(tyreGroups).forEach(([tyreName, points]) => {
      const color = COLORS[colorIndex % COLORS.length];

      // Sort points by pressure for line connection
      points.sort((a, b) => a.x - b.x);

      traces.push({
        x: points.map((p) => p.x),
        y: points.map((p) => p.y),
        mode: 'markers+lines',
        type: 'scatter',
        name: tyreName,
        marker: {
          color: color,
          size: 10,
          symbol: 'circle',
          line: {
            width: 2,
            color: 'white',
          },
        },
        line: {
          color: color,
          width: 2,
          dash: 'solid',
        },
        text: points.map((p) => `Config ${p.config}`),
        hovertemplate:
          '<b>%{fullData.name}</b><br>' +
          'Config: %{text}<br>' +
          `Pressure: %{x:.1f} ${pressureUnit}<br>` +
          'Delta Power: %{y:.3f} W<br>' +
          '<extra></extra>',
      });

      colorIndex++;
    });

    // Add a reference line at y=0
    if (traces.length > 0) {
      const xMin = Math.min(...configData.map((p) => p.x));
      const xMax = Math.max(...configData.map((p) => p.x));

      traces.push({
        x: [xMin - 5, xMax + 5],
        y: [0, 0],
        mode: 'lines',
        type: 'scatter',
        name: 'Baseline (Config 0)',
        line: {
          color: 'gray',
          width: 2,
          dash: 'dash',
        },
        hovertemplate: '<b>Baseline</b><br>' + 'Delta: 0.000 W<br>' + '<extra></extra>',
      });
    }

    return traces;
  }, [session, equipmentCatalog, pressureUnit]);

  // Get baseline info for chart subtitle
  const baselineInfo = useMemo(() => {
    if (!session?.configs) {
      return { tyreName: '', pressurePsi: 0 };
    }

    const baselineConfig = session.configs.find((config: any) => config.letter_number === 0);
    if (!baselineConfig) {
      return { tyreName: '', pressurePsi: 0 };
    }

    // Get tyre name from equipment catalog using front and rear tyre IDs
    const tyreName = baselineConfig.description
      || getCombinedTyreName(baselineConfig.tyre_front_id, baselineConfig.tyre_rear_id, equipmentCatalog);
    const pressure = Math.round(convertPressure(baselineConfig.tyre_rear_pressure, pressureUnit));
    const airspeedKph = Math.round(session.airspeed_ref * 3.6);

    return { tyreName, pressure, airspeedKph };
  }, [session, equipmentCatalog, pressureUnit]);

  // Extract data for separate bar chart delta plots (one per tyre)
  const barDeltaPlotData = useMemo(() => {
    if (!session?.configs) {
      return { traces: [], subplotData: {} };
    }

    // Determine which field to use based on deltaAverage setting
    const deltaField = deltaAverage === 'Moving' ? 'deltas_rolling' : 'deltas';

    // Process data for bar chart
    const configData: any[] = [];

    session.configs.forEach((config: any) => {
      if (!config.runs || config.runs.length === 0) return;

      const tyrePressureBar = config.tyre_rear_pressure;
      const tyreId = config.tyre_rear_id;

      if (!tyrePressureBar || !tyreId) return;

      const tyrePressure = convertPressure(tyrePressureBar, pressureUnit);
      const tyreName = config.description || getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);

      // Use server-provided delta values
      const configValues: number[] = [];

      config.runs.forEach((run: any) => {
        // Use delta or delta_rolling field from server
        const deltaValue = run[deltaField]?.['RollingResistanceNormalised_PG5'];
        if (deltaValue !== null && deltaValue !== undefined) {
          const value = Array.isArray(deltaValue) ? deltaValue[0] : deltaValue;
          configValues.push(value);
        }
      });

      if (configValues.length > 0) {
        // Average the delta values from all runs
        let delta = configValues.reduce((sum, val) => sum + val, 0) / configValues.length;

        // When using constant average, force baseline (letter_number === 0) to zero
        if (deltaAverage === 'Constant' && config.letter_number === 0) {
          delta = 0;
        }

        configData.push({
          tyreName: tyreName,
          pressure: tyrePressure.toFixed(1),
          delta: delta,
          config: config.letter,
          letterNumber: config.letter_number,
        });
      }
    });

    // Group by tyre type for separate subplots
    const tyreGroups: { [tyreName: string]: any[] } = {};
    configData.forEach((point) => {
      if (!tyreGroups[point.tyreName]) {
        tyreGroups[point.tyreName] = [];
      }
      tyreGroups[point.tyreName].push(point);
    });

    // Create data structure for subplots
    const subplotData: { [tyreName: string]: any[] } = {};
    Object.entries(tyreGroups).forEach(([tyreName, points]) => {
      // Sort by pressure
      points.sort((a, b) => parseFloat(a.pressure) - parseFloat(b.pressure));

      subplotData[tyreName] = [
        {
          x: points.map((p) => `${p.pressure} ${pressureUnit}`),
          y: points.map((p) => p.delta),
          type: 'bar',
          name: tyreName,
          hovertemplate:
            '<b>%{fullData.name}</b><br>' + 'Pressure: %{x}<br>' + 'Delta Power: %{y:.3f} W<br>' + '<extra></extra>',
          marker: {
            color: points.map((p) => p.delta),
            colorscale: [
              [0, '#d73027'], // Red for negative values
              [0.5, '#ffffff'], // White for zero
              [1, '#1a9850'], // Green for positive values
            ],
            cmid: 0, // Center colorscale at zero
            line: {
              color: '#333',
              width: 1,
            },
          },
          showlegend: false, // Don't show legend for individual bars
        },
      ];
    });

    return { traces: [], subplotData };
  }, [session, equipmentCatalog, pressureUnit, deltaAverage]);

  // Extract data for temperature vs Crr plot (only configs with more than 1 run)
  const tempCrrPlotData = useMemo(() => {
    if (!session?.configs) {
      return [];
    }

    const traces: any[] = [];
    const tyreColorMap: { [tyreName: string]: string } = {};
    let globalColorIndex = 0;

    session.configs.forEach((config: any) => {
      // Only include configs with more than 1 run
      if (!config.runs || config.runs.length <= 1) {
        return;
      }

      const tyreId = config.tyre_rear_id;
      const tyrePressureBar = config.tyre_rear_pressure;

      if (!tyreId || !tyrePressureBar) {
        return;
      }

      const tyreName = config.description || getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);
      const tyrePressure = convertPressure(tyrePressureBar, pressureUnit);

      // Assign color to tyre type if not already assigned
      if (!tyreColorMap[tyreName]) {
        tyreColorMap[tyreName] = COLORS[globalColorIndex % COLORS.length];
        globalColorIndex++;
      }

      const color = tyreColorMap[tyreName];

      // Extract temperature and Crr delta for each run within this config
      const configPoints: any[] = [];
      config.runs.forEach((run: any, runIndex: number) => {
        const temperature = run.average?.['Temp_TG5']?.[0];
        const crrDelta = run.deltas_self?.['Crr_xG3']?.[0];

        if (temperature !== null && temperature !== undefined && crrDelta !== null && crrDelta !== undefined) {
          configPoints.push({
            x: temperature,
            y: crrDelta,
            config: config.letter,
            pressure: tyrePressure,
            runNumber: runIndex + 1,
          });
        }
      });

      // Only create a trace if there are points for this config
      if (configPoints.length > 0) {
        // Sort points by temperature for proper line connection
        configPoints.sort((a, b) => a.x - b.x);

        traces.push({
          x: configPoints.map((p) => p.x),
          y: configPoints.map((p) => p.y),
          mode: 'markers+lines',
          type: 'scatter',
          name: tyreName,
          legendgroup: tyreName,
          marker: {
            color: color,
            size: 10,
            opacity: 0.7,
            symbol: 'circle',
            line: {
              width: 1,
              color: 'white',
            },
          },
          line: {
            color: color,
            width: 2,
            dash: 'solid',
          },
          text: configPoints.map((p) => `Config ${p.config}, Run ${p.runNumber}`),
          customdata: configPoints.map((p) => p.pressure.toFixed(1)),
          hovertemplate:
            '<b>%{fullData.name}</b><br>' +
            '%{text}<br>' +
            `Pressure: %{customdata} ${pressureUnit}<br>` +
            'Temperature: %{x:.1f} °C<br>' +
            'Crr Delta: %{y:.5f}<br>' +
            '<extra></extra>',
          showlegend: traces.findIndex((t) => t.legendgroup === tyreName) === -1, // Only show legend for first trace of each tyre
        });
      }
    });

    return traces;
  }, [session, equipmentCatalog, pressureUnit]);

  // Extract data for temperature vs Crr_xG5 plot (only configs with more than 1 run)
  const tempCrrG5PlotData = useMemo(() => {
    if (!session?.configs) {
      return [];
    }

    const traces: any[] = [];
    const tyreColorMap: { [tyreName: string]: string } = {};
    let globalColorIndex = 0;

    session.configs.forEach((config: any) => {
      // Only include configs with more than 1 run
      if (!config.runs || config.runs.length <= 1) {
        return;
      }

      const tyreId = config.tyre_rear_id;
      const tyrePressureBar = config.tyre_rear_pressure;

      if (!tyreId || !tyrePressureBar) {
        return;
      }

      const tyreName = config.description || getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);
      const tyrePressure = convertPressure(tyrePressureBar, pressureUnit);

      // Assign color to tyre type if not already assigned
      if (!tyreColorMap[tyreName]) {
        tyreColorMap[tyreName] = COLORS[globalColorIndex % COLORS.length];
        globalColorIndex++;
      }

      const color = tyreColorMap[tyreName];

      // Extract temperature and Crr delta for each run within this config
      const configPoints: any[] = [];
      config.runs.forEach((run: any, runIndex: number) => {
        const temperature = run.average?.['Temp_TG5']?.[0];
        const crrDelta = run.deltas_self?.['Crr_xG5']?.[0];

        if (temperature !== null && temperature !== undefined && crrDelta !== null && crrDelta !== undefined) {
          configPoints.push({
            x: temperature,
            y: crrDelta,
            config: config.letter,
            pressure: tyrePressure,
            runNumber: runIndex + 1,
          });
        }
      });

      // Only create a trace if there are points for this config
      if (configPoints.length > 0) {
        // Sort points by temperature for proper line connection
        configPoints.sort((a, b) => a.x - b.x);

        traces.push({
          x: configPoints.map((p) => p.x),
          y: configPoints.map((p) => p.y),
          mode: 'markers+lines',
          type: 'scatter',
          name: tyreName,
          legendgroup: tyreName,
          marker: {
            color: color,
            size: 10,
            opacity: 0.7,
            symbol: 'circle',
            line: {
              width: 1,
              color: 'white',
            },
          },
          line: {
            color: color,
            width: 2,
            dash: 'solid',
          },
          text: configPoints.map((p) => `Config ${p.config}, Run ${p.runNumber}`),
          customdata: configPoints.map((p) => p.pressure.toFixed(1)),
          hovertemplate:
            '<b>%{fullData.name}</b><br>' +
            '%{text}<br>' +
            `Pressure: %{customdata} ${pressureUnit}<br>` +
            'Temperature: %{x:.1f} °C<br>' +
            'Crr Delta: %{y:.5f}<br>' +
            '<extra></extra>',
          showlegend: traces.findIndex((t) => t.legendgroup === tyreName) === -1, // Only show legend for first trace of each tyre
        });
      }
    });

    return traces;
  }, [session, equipmentCatalog, pressureUnit]);

  // Update raw time plot when data changes (Crr_xG3 - non-temperature-adjusted)
  useEffect(() => {
    const plotElement = rawTimePlotRef.current;
    if (!plotElement) {
      setIsRawTimePlotLoading(false);
      return;
    }

    setIsRawTimePlotLoading(true);

    if (rawTimePlotData && 'traces' in rawTimePlotData && rawTimePlotData.traces.length > 0) {
      const layout = {
        title: {
          text: 'Crr Raw vs Time of Day',
          font: { family: 'Poppins', size: 16 },
        },
        xaxis: {
          title: {
            text: 'Time',
            font: { family: 'Poppins' },
          },
          type: 'date',
          tickformat: '%H:%M',
        },
        yaxis: {
          title: {
            text: 'Crr',
            font: { family: 'Poppins' },
          },
        },
        showlegend: false,
        annotations: [],
        margin: {
          l: 120,
          r: 40,
          t: 100,
          b: 200,
        },
        hovermode: 'closest',
        plot_bgcolor: '#f8f9fa',
        paper_bgcolor: 'white',
        font: { family: 'Poppins' },
      };

      // Create custom legend annotations
      const customLegendAnnotations: any[] = [];
      let legendY = -0.25;

      if (rawTimePlotData && 'customLegendData' in rawTimePlotData) {
        Object.entries(rawTimePlotData.customLegendData).forEach(([tyreName, data]) => {
          customLegendAnnotations.push({
            text: `<b>${tyreName}</b>`,
            x: 0.1,
            y: legendY,
            xref: 'paper',
            yref: 'paper',
            xanchor: 'left',
            yanchor: 'middle',
            font: { family: 'Poppins', size: 14, color: '#333' },
            showarrow: false,
          });

          if (data && typeof data === 'object' && 'pressures' in data && Array.isArray(data.pressures)) {
            data.pressures.forEach((pressure, pressureIndex) => {
              const xPos = 0.35 + pressureIndex * 0.08;
              const color = data.colors?.[pressureIndex];
              const symbol = data.symbols?.[pressureIndex];

              const symbolMap: { [key: string]: string } = {
                circle: '●',
                'triangle-up': '▲',
                square: '■',
                diamond: '◆',
                x: '✕',
              };

              const displayPressure = pressureUnit === 'PSI' ? Math.round(pressure / 0.06895) : pressure;

              customLegendAnnotations.push({
                text: `<b><span style="color: ${color}; font-size: 16px;">${symbolMap[symbol] || '●'}</span>&nbsp;&nbsp;${displayPressure}</b>`,
                x: xPos,
                y: legendY,
                xref: 'paper',
                yref: 'paper',
                xanchor: 'left',
                yanchor: 'middle',
                font: { family: 'Poppins', size: 14, color: '#333' },
                showarrow: false,
              });
            });

            const pressureLabel = pressureUnit === 'PSI' ? 'PSI' : 'Bar';
            const unitXPos = 0.4 + (data.pressures?.length || 0) * 0.08;
            customLegendAnnotations.push({
              text: `<b>(${pressureLabel})</b>`,
              x: unitXPos,
              y: legendY,
              xref: 'paper',
              yref: 'paper',
              xanchor: 'left',
              yanchor: 'middle',
              font: { family: 'Poppins', size: 14, color: '#666' },
              showarrow: false,
            });
          }

          legendY -= 0.06;
        });
      }

      layout.annotations = customLegendAnnotations;

      const config = {
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        displaylogo: false,
        responsive: true,
      };

      if ('traces' in rawTimePlotData) {
        Plotly.newPlot(plotElement, rawTimePlotData.traces, layout, config).then(() => {
          setIsRawTimePlotLoading(false);
        });
      }
    } else {
      Plotly.purge(plotElement);
      setIsRawTimePlotLoading(false);
    }
  }, [rawTimePlotData, pressureUnit]);

  // Update time plot when data changes
  useEffect(() => {
    const plotElement = timePlotRef.current;
    if (!plotElement) {
      setIsTimePlotLoading(false);
      return;
    }

    setIsTimePlotLoading(true);

    if (timePlotData && 'traces' in timePlotData && timePlotData.traces.length > 0) {
      const layout = {
        title: {
          text: 'Attributed Crr vs Time of Day',
          font: { family: 'Poppins', size: 16 },
        },
        xaxis: {
          title: {
            text: 'Time',
            font: { family: 'Poppins' },
          },
          type: 'date',
          tickformat: '%H:%M',
        },
        yaxis: {
          title: {
            text: 'Attributed Crr <br>(Temperature Adjusted)',
            font: { family: 'Poppins' },
          },
        },
        showlegend: false, // Hide default legend
        annotations: [], // Will be populated with custom legend
        margin: {
          l: 120,
          r: 40,
          t: 100,
          b: 200, // Extra space for custom legend
        },
        hovermode: 'closest',
        plot_bgcolor: '#f8f9fa',
        paper_bgcolor: 'white',
        font: { family: 'Poppins' },
      };

      // Create custom legend annotations
      const customLegendAnnotations: any[] = [];
      let legendY = -0.25; // Starting Y position for legend (moved lower)

      if (timePlotData && 'customLegendData' in timePlotData) {
        Object.entries(timePlotData.customLegendData).forEach(([tyreName, data]) => {
          // Add tyre name
          customLegendAnnotations.push({
            text: `<b>${tyreName}</b>`,
            x: 0.1,
            y: legendY,
            xref: 'paper',
            yref: 'paper',
            xanchor: 'left',
            yanchor: 'middle',
            font: { family: 'Poppins', size: 14, color: '#333' },
            showarrow: false,
          });

          // Add pressure symbols and values
          if (data && typeof data === 'object' && 'pressures' in data && Array.isArray(data.pressures)) {
            data.pressures.forEach((pressure, pressureIndex) => {
              const xPos = 0.35 + pressureIndex * 0.08;
              const color = data.colors?.[pressureIndex];
              const symbol = data.symbols?.[pressureIndex];

              // Add symbol (using Unicode approximations)
              const symbolMap: { [key: string]: string } = {
                circle: '●',
                'triangle-up': '▲',
                square: '■',
                diamond: '◆',
                x: '✕',
              };

              // Convert pressure based on selected unit (no unit label here)
              const displayPressure = pressureUnit === 'PSI' ? Math.round(pressure / 0.06895) : pressure;

              customLegendAnnotations.push({
                text: `<b><span style="color: ${color}; font-size: 16px;">${symbolMap[symbol] || '●'}</span>&nbsp;&nbsp;${displayPressure}</b>`,
                x: xPos,
                y: legendY,
                xref: 'paper',
                yref: 'paper',
                xanchor: 'left',
                yanchor: 'middle',
                font: { family: 'Poppins', size: 14, color: '#333' },
                showarrow: false,
              });
            });

            // Add unit label at the end of the row
            const pressureLabel = pressureUnit === 'PSI' ? 'PSI' : 'Bar';
            const unitXPos = 0.4 + (data.pressures?.length || 0) * 0.08;
            customLegendAnnotations.push({
              text: `<b>(${pressureLabel})</b>`,
              x: unitXPos,
              y: legendY,
              xref: 'paper',
              yref: 'paper',
              xanchor: 'left',
              yanchor: 'middle',
              font: { family: 'Poppins', size: 14, color: '#666' },
              showarrow: false,
            });
          }

          legendY -= 0.06; // Move to next row (increased spacing)
        });
      }

      layout.annotations = customLegendAnnotations;

      const config = {
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        displaylogo: false,
        responsive: true,
      };

      if ('traces' in timePlotData) {
        Plotly.newPlot(plotElement, timePlotData.traces, layout, config).then(() => {
          setIsTimePlotLoading(false);
        });
      }
    } else {
      // Clear the plot if no data
      Plotly.purge(plotElement);
      setIsTimePlotLoading(false);
    }
  }, [timePlotData, pressureUnit]);

  // Update delta time plot when data changes
  useEffect(() => {
    const plotElement = deltaTimePlotRef.current;
    if (!plotElement) {
      setIsDeltaTimePlotLoading(false);
      return;
    }

    setIsDeltaTimePlotLoading(true);

    if (deltaTimePlotData && 'traces' in deltaTimePlotData && deltaTimePlotData.traces.length > 0) {
      const layout = {
        title: {
          text: `Crr Deltas vs Time of Day (${deltaAverage} Average)`,
          font: { family: 'Poppins', size: 16 },
        },
        xaxis: {
          title: {
            text: 'Time',
            font: { family: 'Poppins' },
          },
          type: 'date',
          tickformat: '%H:%M',
        },
        yaxis: {
          title: {
            text: 'Attributed Crr Delta<br>(Temperature Adjusted)',
            font: { family: 'Poppins' },
          },
        },
        showlegend: false, // Hide default legend
        annotations: [], // Will be populated with custom legend
        margin: {
          l: 120,
          r: 40,
          t: 100,
          b: 200, // Extra space for custom legend
        },
        hovermode: 'closest',
        plot_bgcolor: '#f8f9fa',
        paper_bgcolor: 'white',
        font: { family: 'Poppins' },
      };

      // Create custom legend annotations
      const customLegendAnnotations: any[] = [];
      let legendY = -0.25; // Starting Y position for legend (moved lower)

      if (deltaTimePlotData && 'customLegendData' in deltaTimePlotData) {
        Object.entries(deltaTimePlotData.customLegendData).forEach(([tyreName, data]) => {
          // Add tyre name
          customLegendAnnotations.push({
            text: `<b>${tyreName}</b>`,
            x: 0.1,
            y: legendY,
            xref: 'paper',
            yref: 'paper',
            xanchor: 'left',
            yanchor: 'middle',
            font: { family: 'Poppins', size: 14, color: '#333' },
            showarrow: false,
          });

          // Add pressure symbols and values
          if (data && typeof data === 'object' && 'pressures' in data && Array.isArray(data.pressures)) {
            data.pressures.forEach((pressure, pressureIndex) => {
              const xPos = 0.35 + pressureIndex * 0.08;
              const color = data.colors?.[pressureIndex];
              const symbol = data.symbols?.[pressureIndex];

              // Add symbol (using Unicode approximations)
              const symbolMap: { [key: string]: string } = {
                circle: '●',
                'triangle-up': '▲',
                square: '■',
                diamond: '◆',
                x: '✕',
              };

              // Convert pressure based on selected unit (no unit label here)
              const displayPressure = pressureUnit === 'PSI' ? Math.round(pressure / 0.06895) : pressure;

              customLegendAnnotations.push({
                text: `<b><span style="color: ${color}; font-size: 16px;">${symbolMap[symbol] || '●'}</span>&nbsp;&nbsp;${displayPressure}</b>`,
                x: xPos,
                y: legendY,
                xref: 'paper',
                yref: 'paper',
                xanchor: 'left',
                yanchor: 'middle',
                font: { family: 'Poppins', size: 14, color: '#333' },
                showarrow: false,
              });
            });

            // Add unit label at the end of the row
            const pressureLabel = pressureUnit === 'PSI' ? 'PSI' : 'Bar';
            const unitXPos = 0.4 + (data.pressures?.length || 0) * 0.08;
            customLegendAnnotations.push({
              text: `<b>(${pressureLabel})</b>`,
              x: unitXPos,
              y: legendY,
              xref: 'paper',
              yref: 'paper',
              xanchor: 'left',
              yanchor: 'middle',
              font: { family: 'Poppins', size: 14, color: '#666' },
              showarrow: false,
            });
          }

          legendY -= 0.06; // Move to next row (increased spacing)
        });
      }

      layout.annotations = customLegendAnnotations;

      const config = {
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        displaylogo: false,
        responsive: true,
      };

      Plotly.newPlot(plotElement, deltaTimePlotData.traces, layout, config).then(() => {
        setIsDeltaTimePlotLoading(false);
      });
    } else {
      // Clear the plot if no data
      Plotly.purge(plotElement);
      setIsDeltaTimePlotLoading(false);
    }
  }, [deltaTimePlotData, pressureUnit, deltaAverage]);

  // Update pressure plot when data changes
  useEffect(() => {
    const plotElement = pressurePlotRef.current;
    if (!plotElement) {
      setIsPressurePlotLoading(false);
      return;
    }

    setIsPressurePlotLoading(true);

    if (pressurePlotData.length > 0) {
      const layout = {
        title: {
          text: 'Crr vs Tyre Pressure',
          font: { family: 'Poppins', size: 16 },
        },
        xaxis: {
          title: {
            text: `Pressure (${pressureUnit})`,
            font: { family: 'Poppins' },
          },
          type: 'linear',
        },
        yaxis: {
          title: {
            text: 'Crr <br>(Temperature Adjusted)',
            font: { family: 'Poppins' },
          },
        },
        showlegend: false, // Hide default legend
        annotations: [], // Will be populated with custom legend
        margin: {
          l: 120,
          r: 40,
          t: 100,
          b: 200, // Extra space for custom legend
        },
        hovermode: 'closest',
        plot_bgcolor: '#f8f9fa',
        paper_bgcolor: 'white',
        font: { family: 'Poppins' },
      };

      // Create custom legend annotations for pressure plot
      const customLegendAnnotations: any[] = [];
      const legendY = -0.25; // Starting Y position for legend
      const legendX = 0.1; // Starting X position

      // Extract unique tyre information from traces
      const tyreInfo: { name: string; color: string }[] = [];
      pressurePlotData.forEach((trace: any) => {
        // Only process average traces (they have name ending with "(average)")
        if (trace.name && trace.name.includes('(average)')) {
          // Remove " (average)" from the name
          const tyreName = trace.name.replace(' (average)', '');
          const color = trace.marker?.color || trace.line?.color || '#000';
          tyreInfo.push({ name: tyreName, color });
        }
      });

      // Add tyre legend entries
      tyreInfo.forEach((tyre, index) => {
        const xPos = legendX + index * 0.3; // Horizontal spacing between tyres

        customLegendAnnotations.push({
          text: `<b><span style="color: ${tyre.color}; font-size: 16px;">●</span>&nbsp;&nbsp;${tyre.name}</b>`,
          x: xPos,
          y: legendY,
          xref: 'paper',
          yref: 'paper',
          xanchor: 'left',
          yanchor: 'middle',
          font: { family: 'Poppins', size: 12, color: '#333' },
          showarrow: false,
        });
      });

      layout.annotations = customLegendAnnotations;

      const config = {
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        displaylogo: false,
        responsive: true,
      };

      Plotly.newPlot(plotElement, pressurePlotData, layout, config).then(() => {
        setIsPressurePlotLoading(false);
      });
    } else {
      // Clear the plot if no data
      Plotly.purge(plotElement);
      setIsPressurePlotLoading(false);
    }
  }, [pressurePlotData, pressureUnit]);

  // Update PSI plot when data changes
  useEffect(() => {
    const plotElement = psiPlotRef.current;
    if (!plotElement) {
      setIsPsiPlotLoading(false);
      return;
    }

    setIsPsiPlotLoading(true);

    if (psiPlotData.length > 0) {
      const layout = {
        title: `Rolling Resistance vs Tyre Pressure (${pressureUnit})`,
        xaxis: {
          title: `Tyre Pressure (${pressureUnit})`,
          type: 'linear',
        },
        yaxis: {
          title: 'Rolling Resistance Normalised',
        },
        legend: {
          x: 0.5,
          y: -0.15,
          xanchor: 'center',
          yanchor: 'top',
          orientation: 'h',
          bgcolor: 'rgba(255, 255, 255, 0.8)',
          bordercolor: '#000',
          borderwidth: 1,
        },
        margin: {
          l: 80,
          r: 40,
          t: 80,
          b: 120,
        },
        hovermode: 'closest',
        plot_bgcolor: '#f8f9fa',
        paper_bgcolor: 'white',
      };

      const config = {
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        displaylogo: false,
        responsive: true,
      };

      Plotly.newPlot(plotElement, psiPlotData, layout, config).then(() => {
        setIsPsiPlotLoading(false);
      });
    } else {
      // Clear the plot if no data
      Plotly.purge(plotElement);
      setIsPsiPlotLoading(false);
    }
  }, [psiPlotData, pressureUnit]);

  // Update delta plot when data changes
  useEffect(() => {
    const plotElement = deltaPlotRef.current;
    if (!plotElement) {
      setIsDeltaPlotLoading(false);
      return;
    }

    setIsDeltaPlotLoading(true);

    if (deltaPlotData.length > 0) {
      const layout = {
        title: {
          text: 'Power Delta from Baseline (Config 0)',
          font: { family: 'Poppins', size: 16 },
        },
        xaxis: {
          title: {
            text: `Tyre Pressure (${pressureUnit})`,
            font: { family: 'Poppins' },
          },
          type: 'linear',
        },
        yaxis: {
          title: {
            text: 'Delta Power (W)',
            font: { family: 'Poppins' },
          },
          zeroline: true,
          zerolinewidth: 2,
          zerolinecolor: 'gray',
        },
        legend: {
          x: 0.5,
          y: -0.15,
          xanchor: 'center',
          yanchor: 'top',
          orientation: 'h',
          bgcolor: 'rgba(255, 255, 255, 0.8)',
          bordercolor: '#000',
          borderwidth: 1,
        },
        margin: {
          l: 80,
          r: 40,
          t: 80,
          b: 120,
        },
        hovermode: 'closest',
        plot_bgcolor: '#f8f9fa',
        paper_bgcolor: 'white',
        font: { family: 'Poppins' },
      };

      const config = {
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        displaylogo: false,
        responsive: true,
      };

      Plotly.newPlot(plotElement, deltaPlotData, layout, config).then(() => {
        setIsDeltaPlotLoading(false);
      });
    } else {
      // Clear the plot if no data
      Plotly.purge(plotElement);
      setIsDeltaPlotLoading(false);
    }
  }, [deltaPlotData, pressureUnit]);

  // Update temperature vs Crr plot when data changes
  useEffect(() => {
    const plotElement = tempCrrPlotRef.current;
    if (!plotElement) {
      setIsTempCrrPlotLoading(false);
      return;
    }

    setIsTempCrrPlotLoading(true);

    if (tempCrrPlotData.length > 0) {
      const layout = {
        title: {
          text: 'Temperature vs Crr Raw Delta (Self Comparison)',
          font: { family: 'Poppins', size: 16 },
        },
        xaxis: {
          title: {
            text: 'Temperature (°C)',
            font: { family: 'Poppins' },
          },
          type: 'linear',
        },
        yaxis: {
          title: {
            text: 'Crr Delta (Self)',
            font: { family: 'Poppins' },
          },
        },
        legend: {
          x: 0.5,
          y: -0.15,
          xanchor: 'center',
          yanchor: 'top',
          orientation: 'h',
          bgcolor: 'rgba(255, 255, 255, 0.8)',
          bordercolor: '#000',
          borderwidth: 1,
        },
        margin: {
          l: 80,
          r: 40,
          t: 80,
          b: 120,
        },
        hovermode: 'closest',
        plot_bgcolor: '#f8f9fa',
        paper_bgcolor: 'white',
        font: { family: 'Poppins' },
      };

      const config = {
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        displaylogo: false,
        responsive: true,
      };

      Plotly.newPlot(plotElement, tempCrrPlotData, layout, config).then(() => {
        setIsTempCrrPlotLoading(false);
      });
    } else {
      // Clear the plot if no data
      Plotly.purge(plotElement);
      setIsTempCrrPlotLoading(false);
    }
  }, [tempCrrPlotData]);

  // Update temperature vs Crr_xG5 plot when data changes
  useEffect(() => {
    const plotElement = tempCrrG5PlotRef.current;
    if (!plotElement) {
      setIsTempCrrG5PlotLoading(false);
      return;
    }

    setIsTempCrrG5PlotLoading(true);

    if (tempCrrG5PlotData.length > 0) {
      const layout = {
        title: {
          text: 'Temperature vs Crr Temp-Adjusted Delta (Self Comparison)',
          font: { family: 'Poppins', size: 16 },
        },
        xaxis: {
          title: {
            text: 'Temperature (°C)',
            font: { family: 'Poppins' },
          },
          type: 'linear',
        },
        yaxis: {
          title: {
            text: 'Crr Delta (Self)',
            font: { family: 'Poppins' },
          },
        },
        legend: {
          x: 0.5,
          y: -0.15,
          xanchor: 'center',
          yanchor: 'top',
          orientation: 'h',
          bgcolor: 'rgba(255, 255, 255, 0.8)',
          bordercolor: '#000',
          borderwidth: 1,
        },
        margin: {
          l: 80,
          r: 40,
          t: 80,
          b: 120,
        },
        hovermode: 'closest',
        plot_bgcolor: '#f8f9fa',
        paper_bgcolor: 'white',
        font: { family: 'Poppins' },
      };

      const config = {
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        displaylogo: false,
        responsive: true,
      };

      Plotly.newPlot(plotElement, tempCrrG5PlotData, layout, config).then(() => {
        setIsTempCrrG5PlotLoading(false);
      });
    } else {
      // Clear the plot if no data
      Plotly.purge(plotElement);
      setIsTempCrrG5PlotLoading(false);
    }
  }, [tempCrrG5PlotData]);

  const handleExportHtml = async (mode: 'full' | 'concise') => {
    setShowExportPopup(false);

    // Trigger flash animation
    setExportFlash(true);
    setTimeout(() => setExportFlash(false), 300);

    setIsExportPreparing(true);

    // Allow React to render the spinner before starting export
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    // Build title from date
    let formattedDate = '';
    if (session?.date) {
      const parsedDate = parse(session.date, 'yyyy-MM-dd', new Date());
      if (isValid(parsedDate)) {
        formattedDate = parsedDate.toLocaleDateString();
      }
    }
    const exportTitle = formattedDate || 'Tyre Report';
    const filenameBase = formattedDate || 'export';
    const filename = `Tyre_Report_${filenameBase.replace(/ /g, '_').replace(/[/\\:*?"<>|]/g, '-')}.html`;

    // In concise mode, hide the 4 detailed charts before export
    const container = printContainerRef.current;
    const conciseElements = container?.querySelectorAll('[data-concise-hide]');
    if (mode === 'concise') {
      conciseElements?.forEach(el => (el as HTMLElement).style.display = 'none');
    }

    await executeExportHtml(printContainerRef, {
      waitTime: 10000,
      title: exportTitle,
      filename,
      onAfterExport: () => {
        conciseElements?.forEach(el => (el as HTMLElement).style.display = '');
        setIsExportPreparing(false);
      },
    });
  };

  if (!session) {
    return (
      <div className="cr-flex cr-h-64 cr-items-center cr-justify-center">
        <Spinner isFetching={true} />
      </div>
    );
  }

  return (
    <div ref={printContainerRef} className="cr-w-full cr-space-y-8">
      <div className="cr-flex cr-justify-end cr-mb-4 cr-mt-4">
        <button
          onClick={() => setShowExportPopup(true)}
          className={`cr-flex cr-items-center cr-gap-2 cr-px-4 cr-py-2 cr-text-sm cr-font-semibold
            cr-rounded-md cr-transition-all cr-duration-200 focus:cr-outline-none
            focus:cr-ring-2 focus:cr-ring-offset-2 print:cr-hidden ${
            exportFlash
              ? 'cr-bg-green-400 cr-scale-105 cr-shadow-lg'
              : 'cr-bg-green-600 hover:cr-bg-green-700 cr-text-white focus:cr-ring-green-500'
          }`}
          style={exportFlash ? { boxShadow: '0 0 20px rgba(34, 197, 94, 0.6)' } : undefined}
        >
          <HiOutlineDocumentArrowDown className="cr-size-5" />
          <span>Export</span>
        </button>
      </div>

      {/* Export Preparing Spinner */}
      {isExportPreparing && createPortal(
        <div
          className="screen-only"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              border: '4px solid rgba(255, 255, 255, 0.3)',
              borderTop: '4px solid #22c55e',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ color: 'white', marginTop: '16px', fontSize: '18px', fontWeight: 500 }}>
            Exporting HTML...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>,
        document.body
      )}

      {/* Export Mode Popup */}
      {showExportPopup && createPortal(
        <div
          className="screen-only"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}
          onClick={() => setShowExportPopup(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '32px',
              minWidth: '340px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
              Export Tyre Report
            </h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#6b7280' }}>
              Choose export format
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => handleExportHtml('full')}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: 'white',
                  backgroundColor: '#16a34a',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Full
              </button>
              <button
                onClick={() => handleExportHtml('concise')}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: 'white',
                  backgroundColor: '#2563eb',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Concise
              </button>
            </div>
            <button
              onClick={() => setShowExportPopup(false)}
              style={{
                width: '100%',
                marginTop: '12px',
                padding: '8px',
                fontSize: '13px',
                color: '#6b7280',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>,
        document.body
      )}

      {(!timePlotData || !('traces' in timePlotData) || timePlotData.traces.length === 0) &&
      (!deltaTimePlotData || !('traces' in deltaTimePlotData) || deltaTimePlotData.traces.length === 0) &&
      pressurePlotData.length === 0 &&
      psiPlotData.length === 0 &&
      deltaPlotData.length === 0 &&
      Object.keys(barDeltaPlotData.subplotData).length === 0 ? (
        <div className="cr-flex cr-h-64 cr-flex-col cr-items-center cr-justify-center cr-space-y-4 cr-text-gray-600">
          <p className="cr-text-lg cr-font-medium">No tyre plot data available</p>
          {timePlotData?.missingData && (
            <div className="cr-bg-yellow-50 cr-rounded-md cr-p-4 cr-text-sm">
              <p className="cr-text-yellow-800 cr-font-medium">Missing Data:</p>
              <p className="cr-text-yellow-700">{timePlotData.missingData}</p>
            </div>
          )}
          {deltaTimePlotData?.missingData && (
            <div className="cr-bg-yellow-50 cr-rounded-md cr-p-4 cr-text-sm">
              <p className="cr-text-yellow-800 cr-font-medium">Missing Delta Data:</p>
              <p className="cr-text-yellow-700">{deltaTimePlotData.missingData}</p>
            </div>
          )}
          {!timePlotData?.missingData && !deltaTimePlotData?.missingData && (
            <p className="cr-text-sm">
              Ensure your session has configs with required data fields (Crr_xG5, tyre_rear_id, tyre_rear_pressure).
            </p>
          )}
        </div>
      ) : (
        <div className="cr-space-y-8">
          {/* Pressure Unit, Delta Average, and Data Type Selectors */}
          <div className="cr-mb-4 cr-flex cr-flex-wrap cr-items-center cr-justify-start cr-gap-x-12 cr-gap-y-3">
            {/* Pressure Unit Selector */}
            <div className="cr-flex cr-items-center cr-space-x-4">
              <span className="cr-text-sm cr-font-medium cr-text-blue-900">Pressure Unit:</span>
              <div className="cr-flex cr-space-x-3">
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input
                    type="radio"
                    name="pressureUnit"
                    value="Bar"
                    checked={pressureUnit === 'Bar'}
                    onChange={(e) => setPressureUnit(e.target.value as 'Bar' | 'PSI')}
                    className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                  />
                  <span className="cr-text-sm cr-text-blue-900">Bar</span>
                </label>
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input
                    type="radio"
                    name="pressureUnit"
                    value="PSI"
                    checked={pressureUnit === 'PSI'}
                    onChange={(e) => setPressureUnit(e.target.value as 'Bar' | 'PSI')}
                    className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                  />
                  <span className="cr-text-sm cr-text-blue-900">PSI</span>
                </label>
              </div>
            </div>

            {/* Delta Average Selector */}
            <div className="cr-flex cr-items-center cr-space-x-4">
              <span className="cr-text-sm cr-font-medium cr-text-blue-900">Delta Average:</span>
              <div className="cr-flex cr-space-x-3">
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input
                    type="radio"
                    name="deltaAverage"
                    value="Constant"
                    checked={deltaAverage === 'Constant'}
                    onChange={(e) => setDeltaAverage(e.target.value as 'Constant' | 'Moving')}
                    className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                  />
                  <span className="cr-text-sm cr-text-blue-900">Constant Average</span>
                </label>
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input
                    type="radio"
                    name="deltaAverage"
                    value="Moving"
                    checked={deltaAverage === 'Moving'}
                    onChange={(e) => setDeltaAverage(e.target.value as 'Constant' | 'Moving')}
                    className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                  />
                  <span className="cr-text-sm cr-text-blue-900">Nearest Pair</span>
                </label>
              </div>
            </div>

            {/* Data Type Selector (Laps/Traverses) */}
            <div className="cr-flex cr-items-center cr-space-x-4">
              <span className="cr-text-sm cr-font-medium cr-text-blue-900">Display:</span>
              <div className="cr-flex cr-space-x-3">
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input
                    type="radio"
                    name="dataType"
                    value="traverses"
                    checked={dataType === 'traverses'}
                    onChange={(e) => setDataType(e.target.value as 'traverses' | 'laps')}
                    disabled={!isTraverseSession}
                    className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                  />
                  <span className={`cr-text-sm ${!isTraverseSession ? 'cr-text-gray-400' : 'cr-text-blue-900'}`}>
                    Traverses
                  </span>
                </label>
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input
                    type="radio"
                    name="dataType"
                    value="laps"
                    checked={dataType === 'laps'}
                    onChange={(e) => setDataType(e.target.value as 'traverses' | 'laps')}
                    className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                  />
                  <span className="cr-text-sm cr-text-blue-900">Laps</span>
                </label>
              </div>
            </div>
          </div>

          {/* Combined Table and Bar Chart */}
          {Object.keys(barDeltaPlotData.subplotData).length > 0 && (() => {
            // Build table data
            const deltaField = deltaAverage === 'Moving' ? 'deltas_rolling' : 'deltas';
            const tableData: Array<{
              config: string;
              description: string;
              pressure: string;
              crr: number | null;
              deltaWatts: number | null;
              isBaseline: boolean;
            }> = [];

            session.configs?.forEach((config: any) => {
              if (config.exclude_from_plot) return;

              const tyrePressureBar = config.tyre_rear_pressure;
              const tyreId = config.tyre_rear_id;

              if (!tyrePressureBar || !tyreId) return;

              const tyrePressure = convertPressure(tyrePressureBar, pressureUnit);
              const isBaseline = config.letter_number === 0;

              // Get CRR from config.average
              const crr = config.average?.Crr_xG5?.[0] ?? null;

              // Get delta watts from config
              let deltaWatts: number | null = null;
              const deltaValue = config[deltaField]?.['RollingResistanceNormalised_PG5'];
              if (deltaValue !== null && deltaValue !== undefined) {
                deltaWatts = Array.isArray(deltaValue) ? deltaValue[0] : deltaValue;
              }

              // When using constant average, force baseline to zero
              if (deltaAverage === 'Constant' && isBaseline) {
                deltaWatts = 0;
              }

              tableData.push({
                config: config.letter || config.letter_number?.toString() || '',
                description: config.description || '',
                pressure: `${tyrePressure.toFixed(1)} ${pressureUnit}`,
                crr,
                deltaWatts,
                isBaseline,
              });
            });

            return (
              <div className="cr-bg-white cr-rounded-lg cr-shadow cr-p-6">
                {/* Table above, Chart below */}
                <div className="cr-space-y-6">
                  {/* Table Section */}
                  <div className="cr-overflow-x-auto">
                    <table className="cr-min-w-full cr-divide-y cr-divide-gray-200">
                      <thead className="cr-bg-gray-50">
                        <tr>
                          <th className="cr-px-6 cr-py-3 cr-text-left cr-text-xs cr-font-medium cr-text-blue-900 cr-uppercase cr-tracking-wider">
                            Config
                          </th>
                          <th className="cr-px-6 cr-py-3 cr-text-left cr-text-xs cr-font-medium cr-text-blue-900 cr-uppercase cr-tracking-wider">
                            Description
                          </th>
                          <th className="cr-px-6 cr-py-3 cr-text-center cr-text-xs cr-font-medium cr-text-blue-900 cr-uppercase cr-tracking-wider">
                            Pressure
                          </th>
                          <th className="cr-px-6 cr-py-3 cr-text-center cr-text-xs cr-font-medium cr-text-blue-900 cr-uppercase cr-tracking-wider">
                            Coefficient of Rolling Resistance
                          </th>
                          <th className="cr-px-6 cr-py-3 cr-text-center cr-text-xs cr-font-medium cr-text-blue-900 cr-uppercase cr-tracking-wider">
                            Watts vs Baseline
                          </th>
                        </tr>
                      </thead>
                      <tbody className="cr-bg-white cr-divide-y cr-divide-gray-200">
                        {tableData.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="cr-px-6 cr-py-4 cr-text-center cr-text-gray-500">
                              No data available
                            </td>
                          </tr>
                        ) : (
                          tableData.map((row, rowIndex) => (
                            <tr key={rowIndex} className={row.isBaseline ? 'cr-bg-blue-50' : ''}>
                              <td className="cr-px-6 cr-py-4 cr-whitespace-nowrap cr-text-sm cr-font-medium cr-text-gray-900">
                                {row.config}
                                {row.isBaseline && (
                                  <span className="cr-ml-2 cr-text-xs cr-text-blue-600 cr-font-semibold">(Baseline)</span>
                                )}
                              </td>
                              <td className="cr-px-6 cr-py-4 cr-whitespace-nowrap cr-text-sm cr-text-gray-700">
                                {row.description}
                              </td>
                              <td className="cr-px-6 cr-py-4 cr-whitespace-nowrap cr-text-sm cr-text-gray-700 cr-text-center">
                                {row.pressure}
                              </td>
                              <td className="cr-px-6 cr-py-4 cr-whitespace-nowrap cr-text-sm cr-text-gray-700 cr-text-center">
                                {row.crr !== null ? row.crr.toFixed(5) : 'N/A'}
                              </td>
                              <td className="cr-px-6 cr-py-4 cr-whitespace-nowrap cr-text-sm cr-text-center">
                                {row.deltaWatts !== null ? (
                                  <span
                                    className={
                                      row.deltaWatts < 0
                                        ? 'cr-text-green-600 cr-font-medium'
                                        : row.deltaWatts > 0
                                        ? 'cr-text-red-600 cr-font-medium'
                                        : 'cr-text-gray-700'
                                    }
                                  >
                                    {row.deltaWatts >= 0 ? '+' : ''}
                                    {row.deltaWatts.toFixed(1)} W
                                  </span>
                                ) : (
                                  'N/A'
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Chart Section */}
                  <div>
                    <TyreDeltaBarChartApex
                      sessionDate={session?.date}
                      sessionDescription={session?.description}
                      airspeedKph={baselineInfo.airspeedKph}
                      riderMassKg={session?.rider_mass}
                      airTemperatureC={session?.air_temperature}
                      baselineTyreName={baselineInfo.tyreName}
                      baselinePressure={baselineInfo.pressure}
                      subplotData={barDeltaPlotData.subplotData}
                      pressureUnit={pressureUnit}
                      deltaAverage={deltaAverage}
                      equipmentCatalog={equipmentCatalog}
                      height={500}
                    />
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Rolling Resistance Scatter Plot with Confidence Intervals */}
          <RollingResistancePlot
            configs={session.configs}
            confidenceLevel={confidenceLevel}
            setConfidenceLevel={setConfidenceLevel}
            session={session}
            dataType={dataType}
            equipmentCatalog={equipmentCatalog}
            yAxisScale={tyreYAxisScale}
            setYAxisScale={setTyreYAxisScale}
          />

          {/* Raw Time-based plot (Crr_xG3) */}
          {rawTimePlotData && 'traces' in rawTimePlotData && rawTimePlotData.traces.length > 0 && (
            <div data-concise-hide className="cr-relative cr-h-[600px] cr-w-full">
              {isRawTimePlotLoading && (
                <LoadingOverlay />
              )}
              <div ref={rawTimePlotRef} className="cr-h-full cr-w-full" />
            </div>
          )}

          {/* Time-based plot */}
          {timePlotData && 'traces' in timePlotData && timePlotData.traces.length > 0 && (
            <div className="cr-relative cr-h-[600px] cr-w-full cr-rounded-xl cr-overflow-hidden cr-shadow-md">
              {isTimePlotLoading && (
                <LoadingOverlay />
              )}
              <div ref={timePlotRef} className="cr-h-full cr-w-full" />
            </div>
          )}

          {/* Delta Time-based plot */}
          {deltaTimePlotData && 'traces' in deltaTimePlotData && deltaTimePlotData.traces.length > 0 && (
            <div className="cr-relative cr-h-[600px] cr-w-full cr-rounded-xl cr-overflow-hidden cr-shadow-md">
              {isDeltaTimePlotLoading && (
                <LoadingOverlay />
              )}
              <div ref={deltaTimePlotRef} className="cr-h-full cr-w-full" />
            </div>
          )}

          {/* Delta plot from baseline (Config 0) */}
          {deltaPlotData.length > 0 && (
            <div data-concise-hide className="cr-relative cr-h-[600px] cr-w-full cr-rounded-xl cr-overflow-hidden cr-shadow-md">
              {isDeltaPlotLoading && (
                <LoadingOverlay />
              )}
              <div ref={deltaPlotRef} className="cr-h-full cr-w-full" />
            </div>
          )}

          {/* Temperature vs Crr Delta plot (Crr_xG3 - Raw) */}
          {tempCrrPlotData.length > 0 && (
            <div data-concise-hide className="cr-relative cr-h-[600px] cr-w-full cr-rounded-xl cr-overflow-hidden cr-shadow-md">
              {isTempCrrPlotLoading && (
                <LoadingOverlay />
              )}
              <div ref={tempCrrPlotRef} className="cr-h-full cr-w-full" />
            </div>
          )}

          {/* Temperature vs Crr Delta plot (Crr_xG5 - Temp-Adjusted) */}
          {tempCrrG5PlotData.length > 0 && (
            <div data-concise-hide className="cr-relative cr-h-[600px] cr-w-full cr-rounded-xl cr-overflow-hidden cr-shadow-md">
              {isTempCrrG5PlotLoading && (
                <LoadingOverlay />
              )}
              <div ref={tempCrrG5PlotRef} className="cr-h-full cr-w-full" />
            </div>
          )}

          {/* Detailed data table */}
          <TyrePrintDataTable configs={session.configs} session={session} />
        </div>
      )}
    </div>
  );
}
